<template>
    <div>
        <ModalMoreDetailsGeneric :moreDetails="moreDetails" title="More Details" />
        <ModalAddNote
            v-if="!isSearchDemo"
            ref="modalAddNote"
            @closeModalAddNote="closeModalAddNote"
            :part="getSelectedPart"
            :parts="substituteParts"
        />

        <ModalEditCustomerReference @doSubmit="updateCustomerRef" :customerItems="customerItems" />

        <ModalShowParts @doCopy="copyParts" :partsTextValue="partsText" />

        <ModalEditPart @doSearch="doUpdateSearch" :partsArray="partsArray" />

        <ModalConfirm
            :text="inventoryQuoteLiveSearchWarningText"
            @confirm="confirmInventoryQuoteBeforeLiveSearchComplete"
            @close="closeInventoryQuoteLiveSearchConfirm"
        />

        <QuoteModal
            ref="quoteModal"
            v-if="displayQuoteModal && !isSearchDemo"
            :data="quoteDetais"
            :searchactionid="search_action_id"
            :quoteid="quoteData[0]?.id ?? null"
            :parts_and_quantities="partsAndQuantities"
            :selected_tab="selected"
            @hide-quote="toggleQuoteModal"
            @quote-submitted="quoteSubmitted"
            :isBpaNoquote="is_bpa_noquote"
        />

        <PricingActionModal
            v-if="!isSearchDemo"
            :paDescription="pa_description"
            :qMargin="pa_current_margin"
            :paSuggestedMargin="pa_suggested_margin"
            :qQuote="pa_current_quote"
            :paSuggestedPrice="pa_suggested_price"
            @doUseSuggestedPrice="pricingActionUseSuggested"
            @doEditPriceManually="pricingActionEditManually"
            @doContinueWithYourPrice="showPricingActionReasonModal"
        />

        <PricingActionReasonModal
            v-if="!isSearchDemo"
            :paReason="pa_reason"
            @update:paReason="pa_reason = $event"
            @doClose="closePricingActionReasonModal"
            @doUpdateReason="pricingActionContinueWithReason"
        />

        <QuotedResponseModal
            v-if="!isSearchDemo"
            @downloadQuoteData="downloadQuoteData"
            :partQuoted="quotedResponse"
            @close="resetSearch"
        />

        <PartQuotedModal v-if="!isSearchDemo" />

        <FuzzyMatchModal
            :part_selected="getSelectedPart"
            :fuzzyPartMatches="getFuzzyPartsMatches[getSelectedPart]"
            @replaceFuzzyMatch="replaceFuzzyMatch"
            @researchFuzzyMatch="researchFuzzyMatch"
        />
        <ModalAlertBanner
            v-if="!isSearchDemo"
            @dismiss="dismissModalAlertBanner"
            :bannerAlerts="bannerAlerts"
        />
        <TopBannerAlert v-if="!isSearchDemo" :part_selected="getSelectedPart" />
        <div class="mt-4 pt-4 d-flex justify-content-between" v-if="loading">
            <div class="spinner-border text-danger m-auto" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
        <div v-else>
            <div v-if="getDisplayData">
                <div
                    v-if="showCustomerSummary"
                    class="mt-3 mb-1 small text-muted customer-summary-line"
                >
                    Customer:
                    <strong class="customer-highlight-target">{{
                        customerHighlightTarget || 'N/A'
                    }}</strong>
                    <span class="mx-2">|</span>
                    Customer Ref:
                    <strong class="customer-highlight-target">{{
                        customerReferenceTarget || 'N/A'
                    }}</strong>
                </div>
                <MultiPartSelector
                    :parts="parts_array"
                    :selectedPart="getSelectedPart"
                    :loading="isPartsLoading"
                    :loadingMessage="activeLiveSearchStreams ? '' : loadingMessage"
                    @setPartSelected="setPartSelected"
                    @resetSearch="resetSearch"
                    @addNote="addNote"
                    :allow-add-note="!isSearchDemo"
                >
                    <template
                        v-if="liveSearchRuns.length || isResumingSearch || liveSearchResumeFailed"
                        #status
                    >
                        <LiveSearchStatus
                            :runs="liveSearchRuns"
                            :loading="isResumingSearch"
                            :resume-failed="liveSearchResumeFailed"
                        />
                    </template>
                </MultiPartSelector>

                <div class="mt-4 pt-4 d-flex justify-content-between" v-if="loading">
                    <div class="spinner-border text-danger m-auto" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>

                <AVGHeader
                    v-else-if="!isSearchDemo"
                    :avg_quote="getStatsLocal.avg_quote"
                    :avg_price="getStatsLocal.avg_price"
                    :avg_margin="getStatsLocal.avg_margin"
                    :won="getStatsLocal.won"
                />

                <SearchTabs
                    v-if="!isEmptyPart"
                    :selected="selected"
                    :salesCount="salesTable.length"
                    :totalQuotes="quotesTable.length"
                    :totalOpenSalesOrders="openSalesOrdersTable.length"
                    :totalOpenPurchaseOrders="openPurchaseOrdersTable.length"
                    :totalInventory="inventoryTable.length"
                    :totalVendor="vendorsTable.length"
                    :totalMarketplaceParts="marketPlaceTable.length"
                    :totalGoogleSearch="googleSearchTable.length"
                    :totalParts="partsVariousSourcesTable.length"
                    :totalStats="getStatsTableCount"
                    :totalNotes="calculateTotals.notes"
                    :hidden-tabs="hiddenSearchTabs"
                    @setTab="setTab"
                />
                <div v-if="!isEmptyPart">
                    <div v-if="selected === 'Sales' && salesTable.length > 0">
                        <SalesTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="salesTable"
                            @more-details="showMoreDetailsSales"
                        />
                    </div>

                    <div v-if="selected === 'Quotes' && quotesTable.length > 0">
                        <QuotesTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="quotesTable"
                            @more-details="showMoreDetailsQuotes"
                        />
                    </div>

                    <div v-if="selected === 'Inventory' && inventoryTable.length > 0">
                        <InventoryTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="inventoryTable"
                            :read-only="isSearchDemo"
                            @toggleQuoteModal="triggerInventoryQuoteModal"
                        />
                    </div>

                    <div v-if="selected === 'Open Sales Orders' && openSalesOrdersTable.length > 0">
                        <OpenSalesOrdersTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="openSalesOrdersTable"
                            @more-details="showMoreDetailsSales"
                        />
                    </div>

                    <div
                        v-if="
                            selected === 'Open Purchase Orders' &&
                            openPurchaseOrdersTable.length > 0
                        "
                    >
                        <OpenPurchaseOrdersTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="openPurchaseOrdersTable"
                            @more-details="showMoreDetailsSales"
                        />
                    </div>

                    <div v-if="selected === 'Google Search' && googleSearchTable.length > 0">
                        <GoogleSearchTable :data="googleSearchTable" />
                    </div>
                    <div
                        v-if="
                            selected === 'Parts (Various Sources)' &&
                            partsVariousSourcesTable.length > 0
                        "
                    >
                        <PartsVariousSourcesTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="partsVariousSourcesTable"
                            :read-only="isSearchDemo"
                            :list-price-only="isSearchDemo"
                            @more-details="showMoreDetailsSales"
                            @toggleQuoteModal="tiggerDisplayQuoteModal"
                        />
                    </div>
                    <div v-if="selected === 'Marketplace Parts' && marketPlaceTable.length > 0">
                        <PartsVariousSourcesTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="marketPlaceTable"
                            :read-only="isSearchDemo"
                            :list-price-only="isSearchDemo"
                            @more-details="showMoreDetailsSales"
                            @toggleQuoteModal="tiggerDisplayQuoteModal"
                        />
                    </div>
                    <div v-if="selected === 'Stats' && getStatsTableCount > 0">
                        <StatsTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="getStatsTables"
                        />
                    </div>

                    <div v-if="selected === 'Vendor'">
                        <VendorTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="vendorsTable"
                            :read-only="isSearchDemo"
                            :list-price-only="isSearchDemo"
                            @more-details="showMoreDetails"
                            @toggleQuoteModal="tiggerDisplayQuoteModal"
                        />
                    </div>

                    <div v-if="selected === 'Notes' && calculateTotals.notes > 0">
                        <NotesTable
                            :part_selected="getSelectedPart"
                            :highlighted-customer="customerHighlightTarget"
                            :data="getTables.notes"
                        />
                    </div>
                </div>
                <div v-else>
                    <div class="alert alert-info mt-4 bg-white border-0 text-center" role="alert">
                        <div class="d-flex justify-content-between" v-if="loading || isPartsLoading">
                            <div
                                class="d-flex justify-content-center align-items-center"
                                style="width: 100%"
                            >
                                <div
                                    class="mx-2 d-flex justify-content-between"
                                    style="height: 40px"
                                    v-if="isPartsLoading"
                                >
                                    <div class="spinner-border text-danger m-auto" role="status">
                                        <span class="sr-only">Loading...</span>
                                    </div>
                                </div>
                                <div
                                    class="d-flex align-items-center"
                                    style="height: 40px"
                                    v-if="isPartsLoading"
                                >
                                    Loading ...
                                </div>
                            </div>
                        </div>
                        <div v-else>No data found for the selected part.</div>
                    </div>
                </div>
            </div>
            <div v-else>
                <SearchForm
                    :initialData="formData"
                    :customers="customerItems"
                    @update="handleFormUpdate"
                    @trigger-search="search"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import '@/assets/dashboard-style.css'
    import SearchForm from '@/components/shared/SearchForm.vue'
    import AVGHeader from '@/components/shared/AVGHeader.vue'
    import MultiPartSelector from '@/components/shared/MultiPartSelector.vue'
    import LiveSearchStatus from '@/components/shared/LiveSearchStatus.vue'
    import TopBannerAlert from '@/components/shared/TopBannerAlert.vue'
    import SearchTabs from '@/components/shared/SearchTabs.vue'

    import SalesTable from '@/components/tabs/SalesTable.vue'
    import QuotesTable from '@/components/tabs/QuotesTable.vue'
    import OpenSalesOrdersTable from '@/components/tabs/OpenSalesOrdersTable.vue'
    import OpenPurchaseOrdersTable from '@/components/tabs/OpenPurchaseOrdersTable.vue'
    import InventoryTable from '@/components/tabs/InventoryTable.vue'
    import VendorTable from '@/components/tabs/VendorTable.vue'
    import GoogleSearchTable from '@/components/tabs/GoogleSearchTable.vue'
    import PartsVariousSourcesTable from '@/components/tabs/PartsVariousSourcesTable.vue'
    import StatsTable from '@/components/tabs/StatsTable.vue'
    import NotesTable from '@/components/tabs/NotesTable.vue'

    import moment from 'moment'

    import SearchService from '@/services/search.service'
    import { followLiveSearch } from '@/services/liveSearchRecovery'
    import CSVService from '@/services/csv.service'
    import NoteService from '@/services/note.service'
    import SearchActionService from '@/services/searchAction.service'
    import QuoteService from '@/services/quotes.service'
    import CustomerService from '@/services/customer.service'
    import config from '@/services/config'

    import { Modal } from 'bootstrap'

    import { mapStores, mapActions as mapPiniaActions } from 'pinia'
    import { parseNumeric } from '@/helpers/string.js'

    import ColsTextron from '@/data/ColsTextron.js'
    import ModalMoreDetailsGeneric from '../modals/ModalMoreDetailsGeneric.vue'
    import ModalAddNote from '../modals/ModalAddNote.vue'
    import ModalAlertBanner from '../modals/ModalAlertBanner.vue'
    import ModalShowParts from '../modals/ModalShowParts.vue'
    import ModalEditPart from '../modals/ModalEditPart.vue'
    import ModalEditCustomerReference from '../modals/ModalEditCustomerReference.vue'
    import FuzzyMatchModal from '../modals/FuzzyMatchModal.vue'
    import PricingActionModal from '../modals/PricingActionModal.vue'
    import PricingActionReasonModal from '../modals/PricingActionReasonModal.vue'
    import ModalConfirm from '../modals/ModalConfirm.vue'

    import QuoteModal from '@/components/shared/QuoteModal.vue'
    import QuotedResponseModal from '@/components/shared/QuotedResponseModal.vue'
    import PartQuotedModal from '@/components/shared/PartQuotedModal.vue'

    import tabsData from '@/data/tabsData.js'
    import {
        getPartCandidates,
        getListPriceMeta,
        getYourPriceMeta,
        isInventoryRow,
        isTextronVendor,
        normalizeVendorRow,
    } from '@/utils/vendorRow'
    import { createKeyboardShortcuts } from '@/utils/keyboardShortcuts'
    import { getRouteShortcutRuntime } from '@/utils/shortcutRegistry'

    import { toast } from 'vue3-toastify'
    import bannerType from '@/data/bannerType.js'

    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import { useSearchStore } from '@/stores/searchStore'
    import { useProfileStore } from '@/stores/profileStore'
    import { useSessionStore } from '@/stores/sessionStore'
    import { useUserStore } from '@/stores/userStore'
    import { useSubstitutesStore } from '@/stores/substitutesStore'
    import { useSubmitQuoteModalStore } from '@/stores/submitQuoteModalStore'

    export default {
        name: 'DashboardBase',
        inject: ['appShell'],
        components: {
            SearchForm,
            SalesTable,
            QuotesTable,
            OpenSalesOrdersTable,
            OpenPurchaseOrdersTable,
            InventoryTable,
            VendorTable,
            GoogleSearchTable,
            PartsVariousSourcesTable,
            StatsTable,
            AVGHeader,
            ModalAddNote,
            MultiPartSelector,
            LiveSearchStatus,
            SearchTabs,
            QuoteModal,
            QuotedResponseModal,
            PartQuotedModal,
            NotesTable,
            ModalShowParts,
            ModalEditPart,
            ModalEditCustomerReference,
            ModalMoreDetailsGeneric,
            FuzzyMatchModal,
            PricingActionModal,
            PricingActionReasonModal,
            TopBannerAlert,
            ModalAlertBanner,
            ModalConfirm,
        },
        computed: {
            activeLiveSearchStreams() {
                return this.liveSearchRuns.filter((run) => !run.complete && !run.stopped).length
            },
            isPartsLoading() {
                return this.loadingParts || this.activeLiveSearchStreams > 0
            },
            ...mapStores(
                useDropdownsStore,
                useProfileStore,
                useSessionStore,
                useUserStore,
                useSearchStore,
                useSubstitutesStore,
                useSubmitQuoteModalStore
            ),
            getDisplayData() {
                return this.searchStore.getDisplayData
            },
            getStats() {
                return this.searchStore.getStats
            },
            calculateTotals() {
                return this.searchStore.calculateTotals
            },
            getTables() {
                return this.searchStore.getTables
            },
            getPartDataGoogleSearch() {
                return this.searchStore.getPartDataGoogleSearch
            },
            getPartDataBoeing() {
                return this.searchStore.getPartDataBoeing
            },
            getPartDataAeroPerformance() {
                return this.searchStore.getPartDataAeroPerformance
            },
            getPartDataAeroFastener() {
                return this.searchStore.getPartDataAeroFastener
            },
            getPartDataTextron() {
                return this.searchStore.getPartDataTextron
            },
            getPartDataTextronDefense() {
                return this.searchStore.getPartDataTextronDefense
            },
            getPartsBase() {
                return this.searchStore.getPartsBase
            },
            getSelectedPart() {
                return this.searchStore.getSelectedPart
            },
            getQuoteData() {
                return this.searchStore.getQuoteData
            },
            getSalesData() {
                return this.searchStore.getSalesData
            },
            getInventoryData() {
                return this.searchStore.getInventoryData
            },
            getOpenSalesOrdersData() {
                return this.searchStore.getOpenSalesOrdersData
            },
            getOpenPurchaseOrdersData() {
                return this.searchStore.getOpenPurchaseOrdersData
            },
            getStatsNumData() {
                return this.searchStore.getStatsNumData
            },
            getSalesNotes() {
                return this.searchStore.getSalesNotes
            },
            getVendors() {
                return this.searchStore.getVendors
            },
            getFuzzyPartsMatches() {
                return this.searchStore.getFuzzyPartsMatches
            },
            getPartsArray() {
                return this.searchStore.getPartsArray
            },
            getPartData() {
                return this.searchStore.getPartData
            },
            getSearchActionId() {
                return this.searchStore.getSearchActionId
            },
            getCustomer() {
                return this.searchStore.getCustomer
            },
            getQuoteType() {
                return this.searchStore.getQuoteType
            },
            getCustomerRef() {
                return this.searchStore.getCustomerRef
            },
            getPartsSearched() {
                return this.searchStore.getPartsSearched
            },
            getSelectedPartIndex() {
                return this.searchStore.getSelectedPartIndex
            },
            getSearchQuantities() {
                return this.searchStore.getQuantities
            },
            getQuantitiesSearchActionId() {
                return this.searchStore.getQuantitiesSearchActionId
            },
            showPartsBase() {
                return this.profileStore.includePartsBase
            },
            isSearchDemo() {
                return config.isSearchDemo ? config.isSearchDemo() : false
            },
            hiddenSearchTabs() {
                if (!this.isSearchDemo) {
                    return []
                }
                return [
                    'sales',
                    'quotes',
                    'inventory',
                    'open_sales_orders',
                    'open_purchase_orders',
                    'google_search',
                    'stats',
                    'notes',
                ]
            },
            customerItems() {
                return this.dropdownsStore.customer
            },
            customerHighlightTarget() {
                const formCustomer = (this.formData?.search_customer || '').toString().trim()
                if (formCustomer) {
                    return formCustomer
                }

                const storeCustomer = (this.getCustomer || '').toString().trim()
                if (storeCustomer) {
                    return storeCustomer
                }

                const quoteEntries = Object.values(this.getQuoteData || {})
                for (const entry of quoteEntries) {
                    const rows = entry?.data || []
                    for (const row of rows) {
                        const customer = (row?.customer || '').toString().trim()
                        if (customer) {
                            return customer
                        }
                    }
                }

                return ''
            },
            customerReferenceTarget() {
                const formReference = (this.formData?.search_reference || '').toString().trim()
                if (formReference) {
                    return formReference
                }

                const storeReference = (this.getCustomerRef || '').toString().trim()
                if (storeReference) {
                    return storeReference
                }
                return ''
            },
            showCustomerSummary() {
                if (this.isEmptyPart) return false
                return Boolean(this.customerHighlightTarget || this.customerReferenceTarget)
            },
            isEmptyPart() {
                return (
                    this.salesTable.length === 0 &&
                    this.quotesTable.length === 0 &&
                    this.inventoryTable.length === 0 &&
                    this.openSalesOrdersTable.length === 0 &&
                    this.openPurchaseOrdersTable.length === 0 &&
                    this.vendorsTable.length === 0 &&
                    this.marketPlaceTable.length === 0 &&
                    this.partsVariousSourcesTable.length === 0 &&
                    this.googleSearchTable.length === 0 &&
                    this.getStatsTableCount === 0 &&
                    this.calculateTotals.notes === 0
                )
            },
            vendorsTable() {
                const selectedPart = this.getSelectedPart
                const substituteParts = [
                    ...new Set(this.getPartsSearched[selectedPart] || []),
                ].filter((part) => part && part !== selectedPart)

                let localSubstituteVendorParts = []
                substituteParts.forEach((sub) => {
                    const vendorItems = this.getVendorRowsForPart(sub)
                    vendorItems.forEach((item) => {
                        localSubstituteVendorParts.push(item)
                    })
                })

                const vendorsData = this.getVendorRowsForPart(this.getSelectedPart)
                const mergedVendorsData = [...vendorsData, ...localSubstituteVendorParts]

                return mergedVendorsData
            },
            quotesTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstituteQuoteParts = []
                substituteParts.forEach((sub) => {
                    const saleItems = this.getQuoteData[sub]?.data || []
                    saleItems.forEach((item) => {
                        localSubstituteQuoteParts.push(item)
                    })
                })

                const quotesData = this.getQuoteData[this.getSelectedPart]?.data || []
                const mergedQuotesData = [...quotesData, ...localSubstituteQuoteParts]

                return mergedQuotesData
            },
            salesTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstituteSalesParts = []
                substituteParts.forEach((sub) => {
                    const saleItems = this.getSalesData[sub]?.data || []
                    saleItems.forEach((item) => {
                        localSubstituteSalesParts.push(item)
                    })
                })

                const salesData = this.getSalesData[this.getSelectedPart]?.data || []
                const mergedSalesData = [...salesData, ...localSubstituteSalesParts]

                return mergedSalesData
            },
            openSalesOrdersTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstituteOpenSalesOrders = []
                substituteParts.forEach((sub) => {
                    const soItems = this.getOpenSalesOrdersData[sub]?.data || []
                    soItems.forEach((item) => {
                        localSubstituteOpenSalesOrders.push(item)
                    })
                })

                const openSalesData = this.getOpenSalesOrdersData[this.getSelectedPart]?.data || []
                const mergedOpenSalesData = [...openSalesData, ...localSubstituteOpenSalesOrders]

                return mergedOpenSalesData
            },
            openPurchaseOrdersTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstituteOpenPurchaseOrders = []
                substituteParts.forEach((sub) => {
                    const poItems = this.getOpenPurchaseOrdersData[sub]?.data || []
                    poItems.forEach((item) => {
                        localSubstituteOpenPurchaseOrders.push(item)
                    })
                })

                const openPurchaseData =
                    this.getOpenPurchaseOrdersData[this.getSelectedPart]?.data || []
                const mergedOpenPurchaseData = [
                    ...openPurchaseData,
                    ...localSubstituteOpenPurchaseOrders,
                ]

                return mergedOpenPurchaseData
            },
            inventoryTable() {
                return this.getInventoryData[this.getSelectedPart]?.data || []
            },
            googleSearchTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstituteGoogleSearchParts = []
                substituteParts.forEach((sub) => {
                    const saleItems = this.getPartDataGoogleSearch[sub]?.data || []
                    saleItems.forEach((item) => {
                        localSubstituteGoogleSearchParts.push(item)
                    })
                })

                const googleSearchData =
                    this.getPartDataGoogleSearch[this.getSelectedPart]?.data ?? []

                const mergedGoogleSearchData = [
                    ...googleSearchData,
                    ...localSubstituteGoogleSearchParts,
                ]

                return mergedGoogleSearchData
            },
            marketPlaceTable() {
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localSubstitute = []
                substituteParts.forEach((sub) => {
                    const items = (this.getPartsBase[sub] ?? []).filter((item) =>
                        this.isMarketplacePartSource(item?.source)
                    )
                    items.forEach((item) => {
                        localSubstitute.push(item)
                    })
                })

                const data = (this.getPartsBase[this.getSelectedPart] ?? []).filter((item) =>
                    this.isMarketplacePartSource(item?.source)
                )

                const mergedData = [...data, ...localSubstitute]

                return mergedData
            },
            partsVariousSourcesTable() {
                const substituteParts = [
                    ...new Set(this.getPartsSearched[this.getSelectedPart] || []),
                ].filter((part) => part && part !== this.getSelectedPart)

                let localSubstitute = []
                substituteParts.forEach((sub) => {
                    const items = (this.getPartsBase[sub] ?? []).filter((item) =>
                        this.isLiveSearchPartSource(item?.source)
                    )
                    items.forEach((item) => {
                        localSubstitute.push(item)
                    })
                })

                const data = (this.getPartsBase[this.getSelectedPart] ?? []).filter((item) =>
                    this.isLiveSearchPartSource(item?.source)
                )

                const mergedData = [...data, ...localSubstitute]

                return this.dedupeLiveSearchRows(mergedData)
            },
            partsText() {
                return this.partsArray.join('\n')
            },
            totalSales() {
                let salesData = this.getSalesData[this.getSelectedPart]?.data ?? []
                return salesData.length
            },
            totalVendors() {
                let data = []

                let selectedParts = this.partsArray.filter((part) => part != this.getSelectedPart)

                for (let key in this.getVendors) {
                    this.getVendorRowsForPart(key).forEach((item) => {
                        if (!selectedParts.includes(item.part_number)) {
                            data.push(item)
                        }
                    })
                }

                return data.length
            },
            getStatsLocal() {
                return (
                    this.getStats[this.getSelectedPart]?.summary ?? {
                        avg_quote: 0,
                        avg_price: 0,
                        avg_margin: 0,
                        won: 0,
                    }
                )
            },
            getStatsTableCount() {
                const firstStatsGroup = this.getStatsTables?.statsData?.[0]
                const quoteStatsRows = Array.isArray(firstStatsGroup)
                    ? firstStatsGroup[0]?.data
                    : firstStatsGroup?.[0]?.data

                return Array.isArray(quoteStatsRows) ? quoteStatsRows.length : 0
            },
            getStatsTables() {
                let statsData = this.getStats[this.getSelectedPart] || []

                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                let localData = []
                substituteParts.forEach((sub) => {
                    const statsItems = this.getStats[sub] || []
                    localData.push(statsItems)
                })

                const statsDataArray = []
                const summaryArray = []

                statsDataArray.push({ ...statsData.statsData, part: statsData.part })
                summaryArray.push({ ...statsData.summary, part: statsData.part })

                localData.forEach((item) => {
                    statsDataArray.push({ ...item.statsData, part: item.part })
                    summaryArray.push({ ...item.summary, part: item.part })
                })

                return {
                    statsData: statsDataArray,
                    summary: summaryArray,
                }
            },
            current_year: function () {
                return moment().format('YYYY')
            },
            last_year: function () {
                return moment().subtract(1, 'year').format('YYYY')
            },
            searchData() {
                // Clean the raw search data and return data
                // containing a single datateble for every
                // site-part pair
                let raw_data = [...this.searchDataRaw] // Copy to new array
                let cleaned_search_data = []
                // let research_subs_dict = this.research_subs_dict
                var research_subs_dict = {}
                // Loop through raw data
                for (let raw_idx in raw_data) {
                    let raw_site = raw_data[raw_idx].site
                    let raw_part = raw_data[raw_idx].part
                    let raw_sub = raw_data[raw_idx].substitute_for
                    // Part research could be on substitutes, so keep track of the reverse relations
                    if (raw_sub) {
                        if (!Object.prototype.hasOwnProperty.call(research_subs_dict, raw_sub)) {
                            research_subs_dict[raw_sub] = []
                        }
                        if (!research_subs_dict[raw_sub].includes(raw_part)) {
                            research_subs_dict[raw_sub].push(raw_part)
                        }
                    }
                    let idx_match = -1
                    let idx_overwrite = -1
                    // Compare raw data to cleaned data to see if raw should be appended to clean
                    for (let clean_idx in cleaned_search_data) {
                        let clean_site = cleaned_search_data[clean_idx].site
                        let clean_part = cleaned_search_data[clean_idx].part

                        // Handle inventory data
                        if (
                            (clean_site == raw_site) &
                            this.inventory.includes(clean_site) &
                            (clean_part == raw_part)
                        ) {
                            idx_overwrite = clean_idx
                            break
                        }
                        // Handle quote/sales data
                        if (this.quoteSale.includes(clean_site)) {
                            if ((clean_site == raw_site) & (clean_part == raw_part)) {
                                // idx_overwrite = clean_idx
                                idx_match = clean_idx
                                break
                            }
                        }
                        // Handle search data
                        if (
                            (clean_site == raw_site) &
                            ((clean_part == raw_sub) | (clean_part == raw_part))
                        ) {
                            idx_match = clean_idx
                            break
                        }
                    }
                    if (idx_overwrite > -1) {
                        let overwrite_data = JSON.parse(JSON.stringify(raw_data[raw_idx]))
                        cleaned_search_data[idx_overwrite] = overwrite_data
                    } else if (idx_match < 0) {
                        // Create a new datatable (deep clone the original object)
                        let copy_data = JSON.parse(JSON.stringify(raw_data[raw_idx])) //Object.assign({}, raw_data[raw_idx]) // {...raw_data[raw_idx]}  // Copy to new object
                        cleaned_search_data.push(copy_data)
                    } else {
                        // Append to the existing data
                        let append_data = [...raw_data[raw_idx].data] // Copy to new array
                        cleaned_search_data[idx_match].data.push(...append_data) // Append each element to cleaned data
                        // Remove any Data=None rows
                    }
                }
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.research_subs_dict = research_subs_dict
                return cleaned_search_data
            },
            is_research_purchase() {
                return this.research_purchases_only
            },
            is_generic_research() {
                return this.generic_research_only
            },
            is_bpa_noquote() {
                const storeBpaParts = this.searchStore?.getBpaParts || []
                const localBpaParts = Array.isArray(storeBpaParts) ? storeBpaParts : this.bpa_parts
                return Array.isArray(localBpaParts)
                    ? localBpaParts.includes(this.getSelectedPart)
                    : false
            },
            selectedLabel() {
                let tabAvailable = 'Sales'
                if (this.salesTable.length > 0) {
                    tabAvailable = 'Sales'
                } else if (this.quotesTable.length > 0) {
                    tabAvailable = 'Quotes'
                } else if (this.vendorsTable.length > 0) {
                    tabAvailable = 'Vendor'
                } else if (this.getStatsTables?.statsData?.length > 0) {
                    tabAvailable = 'Stats'
                } else if (this.calculateTotals.notes > 0) {
                    tabAvailable = 'Notes'
                }

                const tab = this.tabs.find((t) => t.key === this.selected)
                const response = tab ? tab.label : tabAvailable

                this.setSelectedTab(response)

                return response
            },
            currentComponent() {
                const tab = this.tabs.find((t) => t.key === this.selected)
                return tab ? tab.component : 'div'
            },
            dbPartData() {
                let part_data = []
                let raw_data = [...this.dbPartDataRaw]
                let part_selected = this.getSelectedPart
                for (let raw_idx in raw_data) {
                    let raw_row = raw_data[raw_idx]
                    let part_number = raw_row.part_number
                    if (part_number == part_selected) {
                        part_data.push(raw_row)
                    }
                }
                return part_data
            },
            quantity_array: function () {
                // Split part numbers text into array
                let quantity_array = this.formData.quantities_text.split(/[ \n\t]/) // All whitespace
                for (let i = quantity_array.length - 1; i >= 0; i--) {
                    // Remove blanks
                    if (quantity_array[i] == '') {
                        quantity_array.splice(i, 1)
                    }
                }
                return quantity_array
            },
            parts_array: {
                get: function () {
                    return this.searchStore.parts_array
                },
                set: function (new_array) {
                    this.searchStore.set_parts_array(new_array)
                },
            },
            clearMasterSubstitutes() {
                return this.substitutesStore.clearMasterSubstitutes
            },
        },
        data() {
            return {
                backendURL: process.env.VUE_APP_BACKEND_URL,
                textDecoder: new TextDecoder(),
                keep_cols_textron: ColsTextron,
                searchVendorParts: [],
                quoteData: [],
                salesData: [],
                statsData: [],
                formData: {
                    is_nsn: false,
                    includeGSA: false,
                    parts_text: '',
                    quantities_text: '',
                    search_reference: '',
                    search_type: '',
                    search_customer: '',
                    due_date: '',
                },
                searchDataRaw: [],
                loading: false,
                loadingParts: false,
                loadingMessage: '',
                selected: 'Sales',
                dbPartDataRaw: [],
                partsArray: [],
                substitutions: [],
                substituteParts: [],
                bannerAlerts: [],

                search_action_id: 0,
                sales_notes: [],
                admin_notes: [],
                dbCapPartDataRaw: [],
                bannerData: [],
                partsbase_pricing: [],
                partsbase_market: [],
                all_fuzzy_part_matches: {},
                response_buffer: '',
                pricing_rule_match: [],
                bpa_parts: [],
                bpa_minimums: {},
                gsa_parts: [],
                gsa_minimums: {},

                partQuoted: [],
                partIndexQuoted: [],

                pn_count: '',
                pn_total: '',
                process: '',
                sub_count: '',
                sub_total: '',

                quotedResponse: {
                    customer: '',
                    parts: [],
                },

                tabs: tabsData,
                tabData: {
                    sales: [],
                    quotes: [],
                    vendor: [],
                    ils: [],
                    marketplace_parts: [],
                    google_search: [],
                    parts: [],
                    substitutes: [],
                    stats: [],
                },
                mainCards: [
                    { title: 'AVG Quote', value: '$0.00' },
                    { title: 'AVG Price', value: '$0.00' },
                    { title: 'AVG Margin', value: '$0.00' },
                    { title: 'Won', value: '0.00%' },
                ],

                partsAndQuantities: [],
                p_part_number: '',
                p_description: '',
                p_quantity: '',
                p_vendor: '',
                p_unit_cost: '',
                p_notes: '',
                p_unit_of_measure: '',
                purchaseData: [],
                part_index: 0,
                duplicate_part_index: -1,
                part_selected: '',

                research_subs_dict: {},
                research_purchases_only: false,
                generic_research_only: false,
                quoteSale: ['Quotes', 'Sales', 'Repair Orders'],
                inventory: [
                    'Greenwood Ponca Inventory',
                    'GlobalParts Inventory',
                    'Inventory ComponentControl',
                    'Inventory GlobalParts',
                ],
                moreDetails: {},
                searching: false,
                all_google_search: {},
                google_search_key: 0,
                displayQuoteModal: false,
                displayQuotedResponseModal: false,
                quoteDetais: {},
                quote_response: {},
                pa_suggested_price: 0,
                pa_suggested_margin: 0,
                pa_description: '',
                pa_reason: '',
                pa_id: 0,
                pa_current_quote: 0,
                pa_current_margin: 0,
                pendingPricingActionResponse: null,
                pendingPricingActionPayload: null,
                pendingPricingActionForm: null,
                pendingPricingAction: null,
                userSelectedTab: false,
                isResumingSearch: false,
                shortcutManager: null,
                vendorSearchCompleted: false,
                liveSearchRuns: [],
                resumeController: null,
                liveSearchResumeFailed: false,
                pendingInventoryQuoteRow: null,
                inventoryQuoteLiveSearchWarningText:
                    'Live search is still running and additional pricing may still populate. Do you want to continue quoting inventory with the currently available pricing?',
            }
        },
        async mounted() {
            this.loadQuoteNoQuoteReasons()
            this.configureLayout()
            const runtime = getRouteShortcutRuntime('dashboard', {
                handlers: {
                    reset_search: () => this.resetSearch(),
                    open_quote_modal: () => this.openQuoteModalShortcut(),
                },
                predicates: {
                    can_open_quote_modal: () => this.getDisplayData,
                },
            })
            this.shortcutManager = createKeyboardShortcuts({
                target: window,
                capture: true,
                blockCtrlMeta: true,
                bindings: [...runtime.bindings, ...this.buildPartSelectionShortcutBindings()],
            })
            this.shortcutManager.start()
            if (this.$route.query.search) {
                this.loading = true
            }
            await this.loadProfileSettings()

            // if (!sessionStorage.getItem("reloaded")) {
            //     sessionStorage.setItem("reloaded", "true");
            //     setTimeout(() => {
            //         window.location.reload();
            //     }, 500);
            //     return;
            // }

            // this.resetSearch();

            // handle view/resume from my quotes page (myListQuotes.vue)
            if (this.$route.query.search) {
                const id = this.$route.query.search
                await this.resumeSearch(id)
            }

            // handle search again from my quotes page (myListQuotes.vue)
            if (this.$route.query.part) {
                const partsArray = this.$route.query.part.split(',')
                const type = this.$route.query.type || ''
                const customer = this.$route.query.customer || ''
                const qtd = typeof this.$route.query.qtd === 'string' ? this.$route.query.qtd : ''
                this.clearMasterSubstitutes()

                this.formData.parts_text = partsArray.join('\n')
                this.formData.search_type = type
                this.formData.search_customer = customer
                this.formData.quantities_text = qtd

                this.substitutesStore.fetchMasterSubstitutes(partsArray)
                this.search()
            }

            if (this.$route.query.research) {
                try {
                    const partRaw = this.$route.query.research
                    const qtdRaw = this.$route.query.qtd

                    const partsArray = JSON.parse(partRaw)
                    const quantitiesArray = JSON.parse(qtdRaw).map((q) => parseInt(q, 10))

                    for (let qty of quantitiesArray) {
                        if (!Number.isInteger(qty)) {
                            alert('Check Quantities:\nOnly integer numbers are allowed.')
                            return
                        }
                    }

                    const type = this.$route.query.type || ''
                    const customer = this.$route.query.customer || ''

                    this.formData.parts_text = partsArray.join(' ')
                    this.formData.search_type = type
                    this.formData.search_customer = customer
                    this.formData.quantities_text = quantitiesArray.join(' ')

                    await this.substitutesStore.fetchMasterSubstitutes(partsArray)
                    this.search()
                } catch (error) {
                    console.error('Error:', error)
                }
            }

            // Rehydrate local form fields when returning to /dashboard without query params.
            if (
                !this.$route.query.search &&
                !this.$route.query.part &&
                !this.$route.query.research &&
                this.getSearchActionId
            ) {
                this.search_action_id = this.getSearchActionId
                const persistedParts = Array.isArray(this.getPartsArray) ? this.getPartsArray : []
                const persistedQuantities = Array.isArray(this.getSearchQuantities)
                    ? this.getSearchQuantities
                    : []

                this.partsArray = persistedParts
                this.partsAndQuantities = this.buildPartsAndQuantities(
                    persistedParts,
                    persistedQuantities
                )

                if (
                    Array.isArray(this.getSearchQuantities) &&
                    this.getSearchQuantities.length > 0
                ) {
                    this.formData.quantities_text = this.getSearchQuantities.join(' ')
                }
                if (persistedParts.length > 0) {
                    this.formData.parts_text = persistedParts.join('\n')
                }
                this.formData.search_customer = this.getCustomer || ''
                this.formData.search_type = this.getQuoteType || ''
                this.formData.search_reference = this.getCustomerRef || ''
                this.updateSearchQueryParam(this.search_action_id)
            }
        },
        beforeUnmount() {
            this.stopLiveSearch()
            this.shortcutManager?.stop?.()
            this.appShell?.resetLayout?.()
        },
        watch: {
            getDisplayData() {
                this.updateLayoutSideBarProps()
            },
            is_bpa_noquote() {
                this.updateLayoutSideBarProps()
            },
            getSelectedPart(newPart, oldPart) {
                if (newPart !== oldPart) {
                    this.quoteDetais = {}
                }
            },
        },
        methods: {
            async loadQuoteNoQuoteReasons() {
                if (this.isSearchDemo) {
                    return
                }
                try {
                    await this.dropdownsStore.fetchNoQuoteReasons()
                } catch (error) {
                    console.error('Error loading no quote reasons:', error)
                }
            },
            configureLayout() {
                this.appShell?.setTopNavbarProps?.({
                    enableGlobalSearch: false,
                })
                this.appShell?.setTopbarHandlers?.({
                    resetSearch: this.resetSearch,
                    copyParts: this.copyParts,
                    showParts: this.showParts,
                    editParts: this.editParts,
                    editCustomerRef: this.editCustomerRef,
                    fuzzyPartsMatches: this.fuzzyPartsMatches,
                    downloadQuote: this.downloadQuoteData,
                    search: this.handleTopNavbarSearch,
                })
                this.appShell?.setSidebarHandlers?.({
                    toggleQuoteModal: this.toggleQuoteModal,
                })
                this.updateLayoutSideBarProps()
            },
            updateLayoutSideBarProps() {
                this.appShell?.setSideBarProps?.({
                    displayQuotebutton: this.getDisplayData && !this.isSearchDemo,
                    isBpaNoquote: this.is_bpa_noquote,
                })
            },
            ...mapPiniaActions(useDropdownsStore, [
                'setDropdowns',
                'setSearchActionCustomer',
                'setSearchActionCustomerRef',
            ]),
            setDisplayData(value) {
                this.searchStore.setDisplayData(value)
            },
            setResetStats() {
                this.searchStore.setResetStats()
            },
            setStats(stats) {
                this.searchStore.setStats(stats)
            },
            setTables(tables) {
                this.searchStore.setTables(tables)
            },
            setTableGoogleSearch(data) {
                this.searchStore.setTableGoogleSearch(data)
            },
            setTableQuotes(data) {
                this.searchStore.setTableQuotes(data)
            },
            setTableSales(data) {
                this.searchStore.setTableSales(data)
            },
            setTableBoeingAviall(data) {
                this.searchStore.setTableBoeingAviall(data)
            },
            setTableAeroPerformance(data) {
                this.searchStore.setTableAeroPerformance(data)
            },
            setTableAeroFastener(data) {
                this.searchStore.setTableAeroFastener(data)
            },
            setTableTextron(data) {
                this.searchStore.setTableTextron(data)
            },
            setTableTextronDefense(data) {
                this.searchStore.setTableTextronDefense(data)
            },
            setTablePartsBase(data) {
                this.searchStore.setTablePartsBase(data)
            },
            setTableStats(data) {
                this.searchStore.setTableStats(data)
            },
            setTableNotes(data) {
                this.searchStore.setTableNotes(data)
            },
            setPartData(payload) {
                this.searchStore.setPartData(payload)
            },
            setPartStatsData(data) {
                this.searchStore.setPartStatsData(data)
            },
            appendTablePartsBase(data) {
                this.searchStore.appendTablePartsBase(data)
            },
            clearAllPartData() {
                this.searchStore.clearAllPartData()
            },
            clearInventoryData() {
                this.searchStore.clearInventoryData()
            },
            clearAllTotals() {
                this.searchStore.clearAllTotals()
            },
            cleanAllTables() {
                this.searchStore.cleanAllTables()
            },
            setSelectedPart(part) {
                this.searchStore.setSelectedPart(part)
            },
            setQuoteData(data) {
                this.searchStore.setQuoteData(data)
            },
            setSalesData(data) {
                this.searchStore.setSalesData(data)
            },
            setInventoryData(data) {
                this.searchStore.setInventoryData(data)
            },
            setOpenSalesOrdersData(data) {
                this.searchStore.setOpenSalesOrdersData(data)
            },
            setOpenPurchaseOrdersData(data) {
                this.searchStore.setOpenPurchaseOrdersData(data)
            },
            setStatsNumData(data) {
                this.searchStore.setStatsNumData(data)
            },
            setSalesNotes(data) {
                this.searchStore.setSalesNotes(data)
            },
            appendToVendors(payload) {
                this.searchStore.appendToVendors(payload)
            },
            setVendors(payload) {
                this.searchStore.setVendors(payload)
            },
            setFuzzyPartsMatches(payload) {
                this.searchStore.setFuzzyPartsMatches(payload)
            },
            setPartsArray(data) {
                this.searchStore.setPartsArray(data)
            },
            setPartsSearched(payload) {
                this.searchStore.setPartsSearched(payload)
            },
            setBanner(data) {
                this.searchStore.setBanner(data)
            },
            setBannerStatus(status) {
                this.searchStore.setBannerStatus(status)
            },
            setSelectedPartIndex(index) {
                this.searchStore.setSelectedPartIndex(index)
            },
            setSelectedQuoteId(quoteId) {
                this.searchStore.setSelectedQuoteId(quoteId)
            },
            setSearchQuantities(searchActionId, quantities) {
                this.searchStore.setSearchQuantities({ searchActionId, quantities })
            },
            clearSearchQuantities() {
                this.searchStore.clearSearchQuantities()
            },
            buildPartsAndQuantities(parts, quantities) {
                const partsList = Array.isArray(parts) ? parts : []
                const normalizedQuantities = Array.isArray(quantities)
                    ? quantities.map((quantity) => {
                          const parsed = parseInt(quantity, 10)
                          return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
                      })
                    : []

                if (!partsList.length) {
                    return []
                }

                if (normalizedQuantities.length === partsList.length) {
                    return partsList.map((part, index) => ({
                        part,
                        quantity: normalizedQuantities[index],
                    }))
                }

                if (normalizedQuantities.length === 1) {
                    return partsList.map((part) => ({
                        part,
                        quantity: normalizedQuantities[0],
                    }))
                }

                return partsList.map((part, index) => ({
                    part,
                    quantity: normalizedQuantities[index] || 1,
                }))
            },
            async loadProfileSettings() {
                if (this.isSearchDemo) {
                    return
                }
                try {
                    await this.profileStore.fetchProfileSettings()
                } catch (error) {
                    console.error('Error:', error)
                }
            },
            async resumeSearch(searchActionId) {
                const normalizedSearchActionId = parseInt(searchActionId, 10) || searchActionId
                this.loading = true
                this.isResumingSearch = true
                this.stopLiveSearch()
                const resumeController = new AbortController()
                this.resumeController = resumeController
                try {
                    this.search_action_id = searchActionId
                    this.userSelectedTab = false
                    this.vendorSearchCompleted = false
                    this.pendingInventoryQuoteRow = null
                    this.setDisplayData(false)
                    this.setSelectedPartIndex(0)
                    this.searchStore.set_part_index(0)
                    this.searchStore.clearVendors()
                    this.searchStore.clearPartsSearched()
                    this.clearAllPartData()
                    this.clearInventoryData()
                    this.searchStore.setTablePartsBase([])
                    this.substituteParts = []
                    this.clearMasterSubstitutes()
                    const dataSearchAction =
                        await this.searchStore.loadSearchActionData(searchActionId)

                    if (resumeController.signal.aborted) return
                    if (!dataSearchAction) throw new Error('Search action could not be loaded')

                    if (!this.isSearchDemo) {
                        await this.submitQuoteModalStore.getQuotesbySearchActionId(
                            normalizedSearchActionId
                        )
                    }

                    let is_nsn = dataSearchAction.is_nsn
                    let parts_array = dataSearchAction.part_number_list
                    let quantity_array = []
                    if (
                        this.getQuantitiesSearchActionId === normalizedSearchActionId &&
                        Array.isArray(this.getSearchQuantities) &&
                        this.getSearchQuantities.length > 0
                    ) {
                        quantity_array = this.getSearchQuantities
                    } else if (this.isSearchDemo) {
                        quantity_array = parts_array.map(() => 1)
                    } else {
                        try {
                            const quotePayload =
                                await CSVService.getQuoteCSVBySearchId(normalizedSearchActionId)
                            const quoteRows = Array.isArray(quotePayload) ? quotePayload : []
                            quantity_array = quoteRows
                                .map((quote) => parseInt(quote?.quantity, 10))
                                .map((quantity) =>
                                    Number.isFinite(quantity) && quantity > 0 ? quantity : 1
                                )
                        } catch (error) {
                            console.error('Failed to load quote quantities for resume', error)
                        }
                    }
                    this.setSearchQuantities(normalizedSearchActionId, quantity_array)
                    let search_type = dataSearchAction.quote_type

                    let search_customer = dataSearchAction.customer
                    let customer_ref = dataSearchAction.customer_ref

                    this.setSearchActionCustomer(search_customer)
                    this.setSearchActionCustomerRef(customer_ref)

                    this.setPartsArray(dataSearchAction.part_number_list)

                    this.displayData = true
                    this.setDisplayData(true)

                    this.searchStore.set_parts_array(dataSearchAction.part_number_list)
                    this.searchStore.set_part_selected(dataSearchAction.part_number_list[0])
                    this.searchStore.set_part_index(0)

                    this.partsArray = dataSearchAction.part_number_list
                    this.setSelectedPart(dataSearchAction.part_number_list[0])
                    if (!this.isSearchDemo) {
                        this.submitQuoteModalStore.setQuotes(
                            this.hydrateQuoteRowIndexes(this.submitQuoteModalStore.quotes)
                        )
                    } else {
                        this.submitQuoteModalStore.setQuotes([])
                    }

                    this.setSelectedPartIndex(0)
                    this.setSelectedQuoteIdForIndex(0)

                    if (!this.isSearchDemo) {
                        this.init_quote_data(dataSearchAction.part_number_list, quantity_array)
                        await this.loadPartsMinimums(parts_array, search_customer)
                    }
                    this.formData.search_customer = search_customer || ''
                    this.formData.search_type = search_type || ''
                    this.formData.search_reference = customer_ref || ''
                    this.formData.is_nsn = !!is_nsn
                    this.formData.quantities_text = quantity_array.join(' ')
                    this.formData.due_date = dataSearchAction.due_date || ''

                    this.partsAndQuantities = this.buildPartsAndQuantities(
                        this.partsArray,
                        quantity_array
                    )

                    await this.substitutesStore.fetchMasterSubstitutes(parts_array)
                    const masterSubstitutes = this.substitutesStore.master_substitutes
                    if (this.isSearchDemo) {
                        this.hydrateSubstituteState(masterSubstitutes, parts_array)
                    }
                    if (!this.isSearchDemo) {
                        await this.fetchQuoteAggregatedData(
                            searchActionId,
                            parts_array,
                            masterSubstitutes,
                            search_customer
                        )
                        await this.fetchInventoryData(searchActionId)
                    }

                    if (resumeController.signal.aborted) return
                    const dataSession =
                        await SearchService.fetchSessionData(normalizedSearchActionId)
                    if (resumeController.signal.aborted) return
                    if (!Array.isArray(dataSession)) {
                        this.liveSearchResumeFailed = true
                        this.loadingParts = false
                        return
                    }
                    const dbResults = []
                    const liveSearchResults = []
                    const normalizedPartMap = this.buildNormalizedPartMap(
                        this.partsArray,
                        this.substituteParts
                    )

                    dataSession.forEach((result) => {
                        if (result.session_type === 'db') {
                            dbResults.push(...(result.results || []))
                        } else if (result.session_type === 'live-search') {
                            liveSearchResults.push(...(result.results || []))
                        }
                    })

                    dbResults.forEach((item) => {
                        if (!item) {
                            return
                        }
                        const normalizedItem = normalizeVendorRow(item)
                        const partNumber = this.resolveVendorPartKey(
                            normalizedItem,
                            normalizedPartMap
                        )

                        if (partNumber && !isInventoryRow(normalizedItem)) {
                            this.appendToVendors({ part: partNumber, data: normalizedItem })
                        }
                    })

                    const partsBaseData = {}
                    liveSearchResults.forEach((item) => {
                        if (!item) {
                            return
                        }
                        const normalizedItem = normalizeVendorRow(item)
                        const rawPart =
                            normalizedItem.part_number || normalizedItem.part_number_manufacturer
                        if (!rawPart) {
                            return
                        }
                        const cleanedPart = rawPart.includes('=') ? rawPart.split('=')[0] : rawPart
                        const mappedPart =
                            this.resolvePartKey(cleanedPart, normalizedPartMap) || cleanedPart

                        if (!partsBaseData[mappedPart]) {
                            partsBaseData[mappedPart] = []
                        }
                        partsBaseData[mappedPart].push(normalizedItem)
                    })

                    for (const part in partsBaseData) {
                        this.setPartData({
                            part,
                            data: this.dedupeLiveSearchRows(partsBaseData[part]),
                            vendor: 'Parts Base',
                        })
                    }

                    if (Object.keys(partsBaseData).length > 0) {
                        await this.loadPartsBaseData()
                    }

                    if (resumeController.signal.aborted) return
                    const liveSessions = dataSession.filter(session => session.session_type === 'live-search')
                    const seenRuns = new Set()
                    liveSessions.forEach(session => {
                        const flowId = session.parameters?.parent_flow_id
                        if (!flowId || seenRuns.has(flowId)) return
                        seenRuns.add(flowId)
                        const run = this.beginLiveSearchStream(flowId)
                        // Count raw saved rows, before display normalization or deduplication.
                        this.handleLiveSearchData(null, run, (session.results || []).length)
                    })
                    if (!liveSessions.length || liveSessions.some(session => !session.parameters?.parent_flow_id)) {
                        this.liveSearchResumeFailed = true
                    }
                    this.loadingParts = false
                    this.loadingMessage = ''

                    this.setTabPriority()

                    return
                } catch (error) {
                    console.error('Could not restore saved search:', error)
                    if (!resumeController.signal.aborted) {
                        this.liveSearchResumeFailed = true
                        this.loadingParts = false
                    }
                } finally {
                    if (!resumeController.signal.aborted) {
                        this.loading = false
                        this.isResumingSearch = false
                    }
                }
            },
            buildNormalizedPartMap(partsArray, substituteParts) {
                const map = {}
                const allParts = [...(partsArray || []), ...(substituteParts || [])]
                allParts.forEach((part) => {
                    const normalized = this.normalizePartNumber(part)
                    if (normalized) {
                        map[normalized] = part
                    }
                })
                return map
            },
            normalizePartNumber(partNumber) {
                return (partNumber || '')
                    .toString()
                    .trim()
                    .toUpperCase()
                    .replace(/[\s-]+/g, '')
            },
            normalizeQuoteIndex(value) {
                if (value === '' || value === null || value === undefined) {
                    return null
                }
                const parsed = Number(value)
                return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
            },
            hydrateQuoteRowIndexes(quotes = this.submitQuoteModalStore.quotes) {
                if (!Array.isArray(quotes) || !Array.isArray(this.partsArray)) {
                    return []
                }

                const usedIndexes = new Set()
                return quotes.map((quote) => {
                    const existingIndex = this.normalizeQuoteIndex(quote?.selected_part_index)
                    if (existingIndex !== null) {
                        usedIndexes.add(existingIndex)
                        return quote
                    }

                    const originalPart =
                        quote?.part_number || (!quote?.part_number && quote?.part_quoted)
                    const normalizedPart = this.normalizePartNumber(originalPart)
                    if (!normalizedPart) {
                        return quote
                    }

                    const selectedPartIndex = this.partsArray.findIndex((part, index) => {
                        return (
                            !usedIndexes.has(index) &&
                            this.normalizePartNumber(part) === normalizedPart
                        )
                    })

                    if (selectedPartIndex < 0) {
                        return quote
                    }

                    usedIndexes.add(selectedPartIndex)
                    return {
                        ...quote,
                        selected_part_index: selectedPartIndex,
                    }
                })
            },
            buildDraftQuoteRows(searchAction = {}) {
                // Fresh searches get draft quote IDs from createSearchAction; seed the modal immediately.
                const searchActionId = searchAction?.id || this.search_action_id
                const draftQuotes = Array.isArray(searchAction?.draft_quotes)
                    ? searchAction.draft_quotes
                    : []

                return draftQuotes.map((quote, index) => ({
                    ...quote,
                    search_action_id: searchActionId,
                    selected_part_index:
                        this.normalizeQuoteIndex(quote?.selected_part_index) ?? index,
                    submitted: Boolean(quote?.submitted),
                }))
            },
            getQuoteRowForPartIndex(index) {
                // Resolve the selected part selector row to its draft quote identity.
                const selectedPartIndex = this.normalizeQuoteIndex(index)
                if (selectedPartIndex === null) {
                    return null
                }

                const quotes = this.hydrateQuoteRowIndexes(this.submitQuoteModalStore.quotes)
                return (
                    quotes.find(
                        (quote) =>
                            this.normalizeQuoteIndex(quote?.selected_part_index) ===
                            selectedPartIndex
                    ) || null
                )
            },
            setSelectedQuoteIdForIndex(index) {
                const quote = this.getQuoteRowForPartIndex(index)
                this.setSelectedQuoteId(quote?.id || null)
            },
            getSubmittedQuoteRowSet(latestPayload = null) {
                // Track submissions by quote ID first so duplicate part numbers do not collapse together.
                const submittedRows = new Set()
                const quotes = Array.isArray(this.submitQuoteModalStore.quotes)
                    ? [...this.submitQuoteModalStore.quotes]
                    : []

                if (latestPayload) {
                    quotes.push(latestPayload)
                }

                this.hydrateQuoteRowIndexes(quotes).forEach((quote) => {
                    if (!quote?.submitted) {
                        return
                    }

                    if (quote?.id) {
                        submittedRows.add('quote:' + quote.id)
                    }

                    const selectedPartIndex = this.normalizeQuoteIndex(quote?.selected_part_index)
                    if (selectedPartIndex !== null) {
                        submittedRows.add('index:' + selectedPartIndex)
                        return
                    }

                    const originalPart =
                        quote.part_number || (!quote.part_number && quote.part_quoted)
                    const normalizedPart = this.normalizePartNumber(originalPart)
                    if (normalizedPart) {
                        submittedRows.add('part:' + normalizedPart)
                    }
                })

                return submittedRows
            },
            findPartIndex(partNumber) {
                if (!Array.isArray(this.partsArray)) {
                    return -1
                }

                const normalizedPart = this.normalizePartNumber(partNumber)
                if (!normalizedPart) {
                    return -1
                }

                return this.partsArray.findIndex(
                    (part) => this.normalizePartNumber(part) === normalizedPart
                )
            },
            isSubmittedPartRow(index, submittedRows) {
                const quote = this.getQuoteRowForPartIndex(index)
                const normalizedPart = this.normalizePartNumber(this.partsArray[index])
                return (
                    (quote?.id && submittedRows.has('quote:' + quote.id)) ||
                    submittedRows.has('index:' + index) ||
                    (normalizedPart && submittedRows.has('part:' + normalizedPart))
                )
            },
            findNextUnsubmittedPartIndex(currentIndex, submittedRows) {
                if (!Array.isArray(this.partsArray) || this.partsArray.length === 0) {
                    return -1
                }

                const startIndex = Number.isInteger(currentIndex) ? currentIndex : -1

                for (let offset = 1; offset <= this.partsArray.length; offset += 1) {
                    const index =
                        (startIndex + offset + this.partsArray.length) % this.partsArray.length

                    if (!this.isSubmittedPartRow(index, submittedRows)) {
                        return index
                    }
                }

                return -1
            },
            resolvePartKey(partNumber, normalizedMap) {
                if (!partNumber) {
                    return ''
                }
                const normalized = this.normalizePartNumber(partNumber)
                if (!normalized) {
                    return ''
                }
                return normalizedMap?.[normalized] || ''
            },
            hydrateSubstituteState(masterSubstitutes, partNumbers = this.partsArray) {
                const masterSubsDict = masterSubstitutes?.master_substitutes || {}
                const allSubstituteParts = Array.isArray(masterSubstitutes?.all_parts)
                    ? masterSubstitutes.all_parts
                    : Object.values(masterSubsDict).flat()
                const searchedParts = new Set(partNumbers || [])

                this.substituteParts = [...new Set(allSubstituteParts)].filter(
                    (part) => part && !searchedParts.has(part)
                )

                Object.entries(masterSubsDict).forEach(([part, substitutes]) => {
                    this.setPartsSearched({
                        part,
                        data: Array.isArray(substitutes) ? substitutes : [],
                    })
                })
            },
            getScopedMasterSubstitutes(masterSubstitutes, partNumber) {
                const masterSubsDict = masterSubstitutes?.master_substitutes || {}
                const directSubstitutes = Array.isArray(masterSubsDict[partNumber])
                    ? masterSubsDict[partNumber]
                    : []
                const allParts = [...new Set([partNumber, ...directSubstitutes].filter(Boolean))]
                const scopedParts = new Set(allParts)
                const substituteParentMap = {}

                Object.entries(masterSubstitutes?.substitute_parent_map || {}).forEach(
                    ([substitute, parent]) => {
                        if (scopedParts.has(substitute) || scopedParts.has(parent)) {
                            substituteParentMap[substitute] = parent
                        }
                    }
                )

                return {
                    master_substitutes: {
                        [partNumber]: directSubstitutes,
                    },
                    flat_substitutes: directSubstitutes,
                    all_parts: allParts,
                    substitute_parent_map: substituteParentMap,
                    substitute_tree: masterSubstitutes?.substitute_tree || [],
                }
            },
            resolveVendorPartKey(item, normalizedMap) {
                const candidates = getPartCandidates(item)
                for (const candidate of candidates) {
                    const mappedPart = this.resolvePartKey(candidate, normalizedMap)
                    if (mappedPart) {
                        return mappedPart
                    }
                }

                return ''
            },
            getVendorRowsForPart(part) {
                // Keep Vendor clean when resuming older searches that were saved before
                // the backend stopped mixing inventory sources into vendor results.
                return (this.getVendors[part] || []).filter((item) => !isInventoryRow(item))
            },
            validateParts() {
                let emptyParts = []
                this.partsArray.forEach((part) => {
                    const hasQuotes =
                        this.getQuoteData[part] &&
                        this.getQuoteData[part].data &&
                        this.getQuoteData[part].data.length > 0
                    const hasSales =
                        this.getSalesData[part] &&
                        this.getSalesData[part].data &&
                        this.getSalesData[part].data.length > 0
                    const hasVendors = this.getVendorRowsForPart(part).length > 0

                    if (!hasQuotes && !hasSales && !hasVendors) {
                        emptyParts.push(part)
                    }
                })

                if (emptyParts.length === 0) {
                    return
                }

                const message = `The searched part "${emptyParts.join(', ')}" had no data. Would you like to search for alternates?`

                this.setBanner({
                    show: true,
                    message: message,
                    onConfirm: () => {
                        this.fuzzyPartsMatches()
                    },
                })
            },
            setSelectedTab(response) {
                this.selected = response
            },
            async replaceFuzzyMatch(data) {
                const { newPart, oldPart } = data

                const originalParts = [...this.partsArray]
                const updatedParts = originalParts.map((part) => {
                    if (part === oldPart) {
                        return newPart[0]
                    }
                    return part
                })

                this.partsArray = updatedParts
                await this.setPartsArray(updatedParts)
                this.formData.parts_text = updatedParts.join('\n')

                const payload = {
                    updatedParts: JSON.parse(JSON.stringify(updatedParts)),
                    newPart: newPart[0],
                    oldPart: oldPart,
                }

                await this.setPartsSearched({ data: [payload.newPart], part: payload.oldPart })
                await this.handleQuoteUpdate([payload.newPart], originalParts, updatedParts)
            },
            researchFuzzyMatch(parts) {
                // Get quantity for the selected part
                const selectedPartObj = this.partsAndQuantities.find(
                    (item) => item.part === this.getSelectedPart
                )
                const quantity = selectedPartObj.quantity ? [selectedPartObj.quantity] : [0]

                window.open(
                    `/dashboard/?research=${JSON.stringify(parts)}&type=Quote&customer=${this.formData.search_customer}&qtd=${JSON.stringify(quantity)}`,
                    '_blank'
                )
            },
            async updateCustomerRef(data) {
                const { customer, reference } = data

                let search_action_id = this.search_action_id
                try {
                    const res = await this.searchStore.updateSearchActionCustomerReference({
                        id: search_action_id,
                        customer: customer,
                        customer_ref: reference,
                    })
                    const ok =
                        res?.id !== undefined && res?.id !== null && res?.customer_ref !== undefined
                    if (!ok) {
                        toast.error('Error saving value.', {
                            position: 'top-right',
                            autoClose: 2000,
                        })
                        return
                    }

                    toast.success('Values saved', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                } catch (e) {
                    toast.error('Error saving value.', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                this.formData.search_customer = customer
                this.formData.search_reference = reference
            },
            editCustomerRef() {
                const modal = new Modal(document.getElementById('editCustomerRefModal'))
                modal.show()
            },
            async fuzzyPartsMatches() {
                const selectedPart =
                    this.getSelectedPart || this.partsArray[this.getSelectedPartIndex]

                if (!selectedPart) {
                    return
                }

                const fuzzyMatches = await SearchService.getFuzzyPartsMatches(selectedPart)

                for (const part in fuzzyMatches || {}) {
                    await this.setFuzzyPartsMatches({ data: fuzzyMatches[part], part: part })
                }

                this.showfuzzyMatchModal()
            },
            async handleUpdateParts(originalParts, updatedParts) {
                this.loadingParts = true
                this.loadingMessage = 'Updating part...'

                try {
                    const changedParts = {}
                    ;(originalParts || []).forEach((part, index) => {
                        if (part !== updatedParts?.[index]) {
                            changedParts[index] = updatedParts?.[index]
                        }
                    })

                    const response = await this.searchStore.updateSearchActionParts({
                        search_action_id: this.search_action_id,
                        part_number_list: originalParts,
                        new_parts_list: updatedParts,
                        changed_parts_dict: changedParts,
                    })

                    const ok =
                        response?.search_action_id !== undefined &&
                        response?.search_action_id !== null &&
                        Array.isArray(response?.part_number_list)

                    if (!ok) {
                        throw new Error('Failed to update search action parts.')
                    }
                } finally {
                    this.loadingParts = false
                    this.loadingMessage = ''
                }
            },
            async handleUpdateQuotePart(masterSubstitutes, partsArray) {
                if (this.isSearchDemo) {
                    this.hydrateSubstituteState(masterSubstitutes, partsArray)
                    return
                }
                this.loadingParts = true
                this.loadingMessage = 'Loading Quotes...'

                try {
                    await this.fetchQuoteAggregatedData(
                        this.search_action_id,
                        partsArray,
                        masterSubstitutes,
                        this.formData.search_customer
                    )

                    await this.loadPartsMinimums(
                        partsArray,
                        this.formData.search_customer,
                        this.quantity_array[0]
                    )
                } finally {
                    this.loadingParts = false
                    this.loadingMessage = ''
                }
            },
            async handleVendorsPart(newPart, masterSubstitutes) {
                this.loadingParts = true
                this.loadingMessage = 'Loading Vendors...'

                const responseVendors = await SearchService.fetchVendorsParts(
                    [newPart],
                    this.search_action_id,
                    masterSubstitutes
                )

                if (!responseVendors || responseVendors.aborted || !responseVendors.reader) {
                    this.loadingParts = false
                    this.loadingMessage = ''
                    return
                }

                await this.handleVendorsData(responseVendors.reader)

                this.loadingParts = false
                this.loadingMessage = ''
            },
            beginLiveSearchStream(flowId = null) {
                const run = {
                    controller: new AbortController(),
                    actionId: this.search_action_id,
                    flowId,
                    resumed: Boolean(flowId),
                    vendors: [],
                    delivery: flowId ? 'checking' : 'connecting',
                    complete: false,
                    stopped: false,
                }
                this.liveSearchRuns.push(run)
                return run
            },
            stopLiveSearch() {
                this.liveSearchRuns.forEach((run) => run.controller.abort())
                this.resumeController?.abort()
                this.liveSearchRuns = []
                this.resumeController = null
                this.liveSearchResumeFailed = false
            },
            async handlePartsbasePart(newPart, masterSubstitutes) {
                await this.startLiveSearch([newPart], masterSubstitutes)
            },
            async handleQuoteUpdate(newParts, originalParts, updatedParts) {
                const partsArray =
                    Array.isArray(updatedParts) && updatedParts.length
                        ? updatedParts
                        : this.partsArray
                const partsToRefresh = (Array.isArray(newParts) ? newParts : [newParts]).filter(
                    Boolean
                )

                // Update the search action
                await this.handleUpdateParts(originalParts || this.partsArray, partsArray)

                await this.substitutesStore.fetchMasterSubstitutes(partsArray)
                const masterSubstitutes = this.substitutesStore.master_substitutes
                this.hydrateSubstituteState(masterSubstitutes, partsArray)

                // Update quote/history data only outside the public search demo.
                if (!this.isSearchDemo) {
                    await this.handleUpdateQuotePart(masterSubstitutes, partsArray)
                }

                const refreshResults = await Promise.allSettled(
                    partsToRefresh.flatMap((part) => {
                        const scopedSubstitutes = this.getScopedMasterSubstitutes(
                            masterSubstitutes,
                            part
                        )
                        return [
                            this.handleVendorsPart(part, scopedSubstitutes),
                            this.handlePartsbasePart(part, scopedSubstitutes),
                        ]
                    })
                )
                refreshResults.forEach((result) => {
                    if (result.status === 'rejected') {
                        console.error('Failed refreshing edited part search data:', result.reason)
                    }
                })
            },
            async doUpdateSearch(payload) {
                const { updatedParts } = payload

                const originalParts = [...this.partsArray]
                const changedPartEntries = Object.entries(updatedParts || {})
                const newPartsArray = originalParts.map((part, index) => {
                    if (index in updatedParts) {
                        return updatedParts[index]
                    }
                    return part
                })

                this.formData.parts_text = newPartsArray.join(' ')
                this.partsArray = newPartsArray
                this.setPartsArray(newPartsArray)
                this.searchStore.set_parts_array(newPartsArray)
                this.partsAndQuantities = this.buildPartsAndQuantities(
                    newPartsArray,
                    this.quantity_array
                )

                changedPartEntries.forEach(([partIndex, changedPart]) => {
                    this.setPartsSearched({
                        data: [changedPart],
                        part: originalParts[partIndex],
                    })
                })

                const selectedPartIndex = this.normalizeQuoteIndex(this.getSelectedPartIndex)
                const nextSelectedPartIndex =
                    selectedPartIndex !== null && selectedPartIndex < newPartsArray.length
                        ? selectedPartIndex
                        : 0
                const nextSelectedPart = newPartsArray[nextSelectedPartIndex]

                this.setSelectedPart(nextSelectedPart)
                this.setSelectedPartIndex(nextSelectedPartIndex)

                this.searchStore.set_parts_array(newPartsArray)
                this.searchStore.set_part_selected(nextSelectedPart)

                await this.handleQuoteUpdate(
                    changedPartEntries.map(([, changedPart]) => changedPart),
                    originalParts,
                    newPartsArray
                )
            },
            editParts() {
                const modal = new Modal(document.getElementById('showEditPartModal'))
                modal.show()
            },
            showParts() {
                const modal = new Modal(document.getElementById('showPartsModal'))
                modal.show()
            },
            async copyParts() {
                if (!navigator.clipboard) {
                    toast.error('Clipboard unavailable in this browser', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                const text = this.partsArray.join('\n').trim()

                try {
                    await navigator.clipboard.writeText(text)
                    toast.success('Copied to clipboard', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                } catch (err) {
                    toast.error('Failed copy', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                }
            },
            async setPartSelected({ part, index }) {
                this.userSelectedTab = false
                this.setSelectedPart(part)
                this.setSelectedPartIndex(index)
                this.setSelectedQuoteIdForIndex(index)

                await this.setTableGoogleSearch([])
                await this.setTableBoeingAviall([])
                await this.setTableAeroPerformance([])
                await this.setTableAeroFastener([])
                await this.setTableTextron([])
                await this.setTableTextronDefense([])

                if (!this.isSearchDemo) {
                    await this.loadSearchQuoteData()
                }
                await this.loadSearchStreammedVendorData()
                await this.loadPartsBaseData()
                if (!this.isSearchDemo) {
                    await this.reloadNotes()
                }

                this.setTabPriority()
            },
            buildPartSelectionShortcutBindings() {
                const bindings = []
                for (let number = 1; number <= 9; number += 1) {
                    bindings.push({
                        combo: `alt+shift+${number}`,
                        action: () => this.selectPartByShortcut(number),
                    })
                }
                return bindings
            },
            async selectPartByShortcut(position) {
                const index = Number(position) - 1
                if (!Number.isInteger(index) || index < 0) {
                    return
                }

                const part = this.parts_array?.[index]
                if (!part) {
                    return
                }

                if (this.getSelectedPartIndex === index) {
                    return
                }

                await this.setPartSelected({ part, index })
            },
            setTabPriority() {
                const selectedHasData =
                    (this.selected === 'Sales' && this.salesTable.length > 0) ||
                    (this.selected === 'Quotes' && this.quotesTable.length > 0) ||
                    (this.selected === 'Inventory' && this.inventoryTable.length > 0) ||
                    (this.selected === 'Open Sales Orders' &&
                        this.openSalesOrdersTable.length > 0) ||
                    (this.selected === 'Open Purchase Orders' &&
                        this.openPurchaseOrdersTable.length > 0) ||
                    (this.selected === 'Vendor' && this.vendorsTable.length > 0) ||
                    (this.selected === 'Stats' && this.getStatsTableCount > 0) ||
                    (this.selected === 'Notes' && this.calculateTotals.notes > 0) ||
                    (this.selected === 'Google Search' && this.googleSearchTable.length > 0) ||
                    (this.selected === 'Marketplace Parts' && this.marketPlaceTable.length > 0) ||
                    (this.selected === 'Parts (Various Sources)' &&
                        this.partsVariousSourcesTable.length > 0)

                if (this.userSelectedTab || selectedHasData) {
                    return
                }
                if (this.salesTable.length > 0) {
                    this.setTab('sales', false)
                } else if (this.quotesTable.length > 0) {
                    this.setTab('quotes', false)
                } else if (this.inventoryTable.length > 0) {
                    this.setTab('inventory', false)
                } else if (this.openSalesOrdersTable.length > 0) {
                    this.setTab('open_sales_orders', false)
                } else if (this.openPurchaseOrdersTable.length > 0) {
                    this.setTab('open_purchase_orders', false)
                } else if (this.vendorsTable.length > 0) {
                    this.setTab('vendor', false)
                } else if (this.partsVariousSourcesTable.length > 0) {
                    this.setTab('parts', false)
                } else if (this.marketPlaceTable.length > 0) {
                    this.setTab('marketplace_parts', false)
                } else if (this.googleSearchTable.length > 0) {
                    this.setTab('google_search', false)
                } else if (this.getStatsTableCount > 0) {
                    this.setTab('stats', false)
                } else if (this.calculateTotals.notes > 0) {
                    this.setTab('notes', false)
                } else {
                    this.setTab('sales', false)
                }
            },
            async loadStreamQuote(searchActionId) {
                const response = await SearchService.streamQuote(searchActionId)

                // this.loading = response.loading
                this.setDisplayData(response.displayData)
                this.searching = response.searching
                this.new_search = response.newSearch

                this.writeToDOM(response.results.getReader())
            },
            async downloadQuoteData() {
                let dataPayload = this.partQuoted
                    .map((item) => item[Object.keys(item)[0]]?.payload)
                    .filter(Boolean)

                if (dataPayload.length === 0) {
                    dataPayload = Array.isArray(this.submitQuoteModalStore.quotes)
                        ? this.submitQuoteModalStore.quotes
                        : []
                }

                if (dataPayload.length === 0 && this.search_action_id) {
                    dataPayload =
                        (await QuoteService.getQuotesBySearchActionId(this.search_action_id)) || []
                }

                if (!Array.isArray(dataPayload) || dataPayload.length === 0) {
                    toast.error('No quote data available for CSV generation.', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                const firstQuote = dataPayload[0] || {}
                const fileName = CSVService.buildQuoteDownloadFileName({
                    quoteType: this.formData?.search_type || firstQuote?.quote_type,
                    customer: this.formData?.search_customer || firstQuote?.customer,
                    customerReference:
                        this.formData?.search_reference ||
                        this.getCustomerRef ||
                        firstQuote?.customer_ref,
                })
                const csv = CSVService.generateQuoteCSV(dataPayload)
                CSVService.downloadCSV(csv, this.formData, fileName)
            },
            async quoteSubmitted(response) {
                const pricingAction = this.getPricingActionFromQuoteResponse(response)
                if (pricingAction && pricingAction.meets_criteria === false) {
                    this.setPricingActionData(pricingAction, response)
                    this.closeQuoteModalForPricingAction()
                    this.showPricingActionModal()
                    return
                }

                await this.completeQuoteSubmission(response)
            },
            async completeQuoteSubmission(response) {
                const payload = response?.payload || {}
                const submittedPart = payload.part_number || this.getSelectedPart
                this.partQuoted.push({ [submittedPart]: response })

                this.quotedResponse.customer = this.formData.search_customer
                this.quotedResponse.parts.push(payload)

                this.quote_response = response
                if (this.displayQuoteModal) {
                    this.toggleQuoteModal()
                }

                const payloadPartIndex = this.normalizeQuoteIndex(payload.selected_part_index)
                const currentPartIndex =
                    payloadPartIndex !== null ? payloadPartIndex : Number(this.getSelectedPartIndex)
                this.partIndexQuoted.push(currentPartIndex)

                const submittedParts = this.getSubmittedQuoteRowSet(payload)
                const nextIndex = this.findNextUnsubmittedPartIndex(
                    currentPartIndex,
                    submittedParts
                )

                if (nextIndex >= 0) {
                    toast.success('Part quoted with success!', {
                        position: 'top-left',
                        autoClose: 2000,
                    })
                    this.setSelectedPartIndex(nextIndex)
                    this.setSelectedPart(this.partsArray[nextIndex])
                    this.setSelectedQuoteIdForIndex(nextIndex)
                } else {
                    this.showQuotedResponseModal()
                    await this.updateAction(this.search_action_id)
                }
            },
            getPricingActionFromQuoteResponse(response) {
                return response?.data?.pricing_action || response?.pricing_action || null
            },
            setPricingActionData(pricingAction, response) {
                const payload = response?.payload || {}
                const form = response?.form || {}
                this.pendingPricingActionResponse = response
                this.pendingPricingActionPayload = payload
                this.pendingPricingActionForm = form
                this.pendingPricingAction = pricingAction
                this.pa_suggested_price = pricingAction.suggested_price
                this.pa_suggested_margin = pricingAction.suggested_margin
                this.pa_description = pricingAction.description
                this.pa_id = pricingAction.pa_id || pricingAction.id || 0
                this.pa_current_quote = form.q_quote || payload.quote || 0
                this.pa_current_margin = form.q_margin || payload.margin || 0
                this.pa_reason = ''
            },
            showPricingActionModal() {
                this.$nextTick(() => {
                    document.getElementById('pricing_action_modal')?.classList.add('is-active')
                })
            },
            closePricingActionModal() {
                document.getElementById('pricing_action_modal')?.classList.remove('is-active')
            },
            showPricingActionReasonModal() {
                document.getElementById('pricing_action_reason_modal')?.classList.add('is-active')
            },
            closePricingActionReasonModal() {
                document
                    .getElementById('pricing_action_reason_modal')
                    ?.classList.remove('is-active')
            },
            closeQuoteModalForPricingAction() {
                if (!this.displayQuoteModal) {
                    return
                }
                this.displayQuoteModal = false
                this.quoteDetais = {}
            },
            buildPricingActionQuoteDetails(overrides = {}) {
                const payload = this.pendingPricingActionPayload || {}
                const form = this.pendingPricingActionForm || {}
                return {
                    ...payload,
                    part_selected:
                        form.part_selected || payload.part_number || this.getSelectedPart,
                    part_number: payload.part_number || form.part_selected || this.getSelectedPart,
                    part_quoted: form.q_part_quoted || payload.part_quoted || payload.part_number,
                    description: form.q_description || payload.description,
                    vendor: form.q_vendor || payload.vendor,
                    condition: form.q_condition || payload.condition,
                    unit_cost: form.q_unit_cost || payload.unit_cost,
                    quantity: form.q_quantity || payload.quantity,
                    list_price: form.q_list_price || payload.list_price,
                    unit_of_measure: form.q_unit_of_measure || payload.unit_of_measure,
                    margin: form.q_margin || payload.margin,
                    lead_time: form.q_lead_time || payload.lead_time,
                    exchange: form.q_exchange ?? payload.exchange,
                    core: form.q_core || payload.core,
                    extra_charge: form.q_extra_charge || payload.extra_charge,
                    extra_charge_type: form.q_extra_charge_type || payload.extra_charge_type,
                    quote: form.q_quote || payload.quote,
                    ext_quote: form.q_ext_quote || payload.ext_quote,
                    notes: form.q_notes || payload.notes,
                    ...overrides,
                }
            },
            calculatePricingActionMargin(price, unitCost) {
                const parsedPrice = Number(price)
                const parsedUnitCost = Number(unitCost)
                if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
                    return this.pendingPricingActionPayload?.margin || ''
                }
                if (!Number.isFinite(parsedUnitCost)) {
                    return this.pendingPricingActionPayload?.margin || ''
                }
                return (((parsedPrice - parsedUnitCost) / parsedPrice) * 100).toFixed(2)
            },
            async pricingActionUseSuggested() {
                const payload = { ...(this.pendingPricingActionPayload || {}) }
                const suggestedPrice = this.pa_suggested_price
                const suggestedMargin = this.calculatePricingActionMargin(
                    suggestedPrice,
                    payload.unit_cost
                )
                payload.quote = Number(suggestedPrice)
                payload.margin = suggestedMargin
                payload.margin_input = suggestedMargin
                payload.no_quote = false
                payload.submitted = true

                try {
                    const data = await QuoteService.submitQuote(payload)
                    const persistedQuote = data?.data || null
                    this.submitQuoteModalStore.upsertQuote(persistedQuote)
                    const nextResponse = {
                        data,
                        payload: data?.payload || payload,
                        form: {
                            ...(this.pendingPricingActionForm || {}),
                            q_quote: suggestedPrice,
                            q_margin: suggestedMargin,
                        },
                    }
                    this.closePricingActionModal()
                    await this.completeQuoteSubmission(nextResponse)
                } catch (error) {
                    alert(error?.message || 'Failed to submit suggested price.')
                }
            },
            pricingActionEditManually() {
                const details = this.buildPricingActionQuoteDetails()
                this.closePricingActionModal()
                this.displayQuoteModal = true
                this.quoteDetais = details
                this.$nextTick(() => {
                    this.$refs.quoteModal?.maximize?.()
                })
            },
            async pricingActionContinueWithReason() {
                const pricingAction = this.pendingPricingAction || {}
                const quoteId =
                    pricingAction.quote_id ||
                    this.pendingPricingActionPayload?.id ||
                    this.pendingPricingActionResponse?.data?.data?.id
                if (!quoteId) {
                    alert('Unable to save pricing reason because quote id is missing.')
                    return
                }

                try {
                    await QuoteService.updatePricingActionReason(quoteId, this.pa_reason)
                    this.closePricingActionReasonModal()
                    this.closePricingActionModal()
                    await this.completeQuoteSubmission(this.pendingPricingActionResponse)
                } catch (error) {
                    alert(error?.message || 'Failed to save pricing reason.')
                }
            },
            async updateAction(search_action_id) {
                if (search_action_id) {
                    await SearchService.markSearchActionFinished(search_action_id)
                }
            },
            getInventoryVendorForQuote(inventoryRow = {}) {
                const warehouseVendorMap = {
                    'GWG-KPNC': 'Greenwood Ponca KPNC',
                    'TXT-MP': 'Greenwood Ponca TXT',
                }
                const warehouse = (inventoryRow?.warehouse || '').toString().trim().toUpperCase()
                if (warehouseVendorMap[warehouse]) {
                    return warehouseVendorMap[warehouse]
                }

                const candidates = [inventoryRow?.source, 'Greenwood Ponca Inventory']
                const preferred = candidates.find((value) => String(value || '').trim())
                return preferred ? String(preferred).trim() : ''
            },
            getTextronLiveSearchRowForPart(partNumber) {
                const normalizedPart = this.normalizePartNumber(partNumber)
                if (!normalizedPart) {
                    return null
                }

                const textronRows = this.partsVariousSourcesTable || []

                return textronRows.map(normalizeVendorRow).find((row) => {
                    const isMatchingPart = getPartCandidates(row).some(
                        (candidate) => this.normalizePartNumber(candidate) === normalizedPart
                    )
                    const textronPrice = getYourPriceMeta(row).value

                    return isTextronVendor(row) && isMatchingPart && textronPrice > 0
                })
            },
            buildInventoryQuoteDetails(inventoryRow = {}) {
                const inventoryPart = inventoryRow?.part_number || ''
                const inventoryVendorForQuote = this.getInventoryVendorForQuote(inventoryRow)
                const textronPricingRow = this.getTextronLiveSearchRowForPart(inventoryPart)
                const textronPrice = textronPricingRow ? getYourPriceMeta(textronPricingRow).value: null
                const textronListPrice = textronPricingRow ? getListPriceMeta(textronPricingRow).value: null
                const inventoryUnitCost = parseNumeric(inventoryRow?.unit_cost, null)
                const replacementCost = textronPrice !== null ? textronPrice : Number.isFinite(inventoryUnitCost) && inventoryUnitCost > 0 ? inventoryUnitCost : null

                return {
                    ...inventoryRow,
                    part_number: inventoryPart || this.getSelectedPart,
                    part_quoted: inventoryPart || this.getSelectedPart,
                    description: inventoryRow?.description || '',
                    vendor: inventoryVendorForQuote,
                    ui_vendor_name: inventoryVendorForQuote,
                    use_prefill_price_values: true,
                    unit_cost: replacementCost,
                    list_price: textronPrice !== null ? textronListPrice : null,
                    uom: inventoryRow?.uom || inventoryRow?.unit_of_measure || '',
                    inventory_source: inventoryRow?.source || '',
                    inventory_warehouse: inventoryRow?.warehouse || '',
                }
            },
            triggerInventoryQuoteModal(inventoryRow = {}) {
                if (this.activeLiveSearchStreams > 0) {
                    this.pendingInventoryQuoteRow = inventoryRow
                    this.showInventoryQuoteLiveSearchConfirm()
                    return
                }

                this.tiggerDisplayQuoteModal(this.buildInventoryQuoteDetails(inventoryRow))
            },
            showInventoryQuoteLiveSearchConfirm() {
                this.$nextTick(() => {
                    const modalElement = document.getElementById('confirmModal')
                    if (!modalElement) {
                        return
                    }

                    const modal = Modal.getInstance(modalElement) || new Modal(modalElement)
                    modal.show()
                })
            },
            confirmInventoryQuoteBeforeLiveSearchComplete() {
                const inventoryRow = this.pendingInventoryQuoteRow || {}
                this.pendingInventoryQuoteRow = null
                this.tiggerDisplayQuoteModal(this.buildInventoryQuoteDetails(inventoryRow))
            },
            closeInventoryQuoteLiveSearchConfirm() {
                this.pendingInventoryQuoteRow = null
            },

            tiggerDisplayQuoteModal(details = {}) {
                if (this.isSearchDemo) {
                    return
                }
                const existingPart = this.partQuoted.find((part) => part[details.part_selected])

                if (existingPart) {
                    alert('Part already quoted.')
                    return
                }

                this.toggleQuoteModal(details)
            },

            async ensureRetailQuoteOwnership() {
                const isRetail = config.isRetail
                    ? config.isRetail()
                    : config.getSubdomain() === 'retail'
                if (!isRetail) {
                    return true
                }

                const assignment = await QuoteService.assignQuoteToMe(this.search_action_id)
                if (assignment?.success) {
                    return true
                }
                if (assignment?.status === 409) {
                    alert('Quote already assigned to another user.')
                    return false
                }

                alert(assignment?.message || 'Unable to assign quote.')
                return false
            },

            async toggleQuoteModal(details = {}) {
                if (this.isSearchDemo) {
                    this.displayQuoteModal = false
                    this.quoteDetais = {}
                    return
                }
                const hasDetails =
                    details && typeof details === 'object' && Object.keys(details).length > 0

                if (
                    (hasDetails || !this.displayQuoteModal) &&
                    !(await this.ensureRetailQuoteOwnership())
                ) {
                    return
                }

                if (hasDetails) {
                    this.displayQuoteModal = true
                    // Replace prefilled payload so modal refreshes with the clicked row.
                    this.quoteDetais = { ...details }
                    this.$nextTick(() => {
                        this.$refs.quoteModal?.maximize?.()
                    })
                    return
                }

                this.displayQuoteModal = !this.displayQuoteModal

                if (!this.displayQuoteModal) {
                    this.quoteDetais = {}
                }
            },
            openQuoteModalShortcut() {
                if (this.isSearchDemo) {
                    return
                }
                if (this.displayQuoteModal) {
                    this.$refs.quoteModal?.toggleMinimize?.()
                    return
                }
                this.toggleQuoteModal()
            },
            addNote() {
                if (this.isSearchDemo) {
                    return
                }
                const modal = new Modal(document.getElementById('addNote')) // Bootstrap modal instance
                modal.show()
            },
            closeModalAddNote() {
                this.reloadNotes()
            },
            processGoogleSearch(googleSearch) {
                if (Object.prototype.hasOwnProperty.call(googleSearch[0].data[0], 'title')) {
                    // format google search data
                    for (let i = 0; i < googleSearch[0].data.length; i++) {
                        // parsed[0].data[i].price = numeral(parsed[0].data[i].unit_cost).format('$0,0,0,0,0.00')
                        let link = googleSearch[0].data[i].link
                        if (link) {
                            if (link.includes('//')) {
                                let s1 = link.split('//')[1]
                                if (link.includes('/')) {
                                    let link_site = s1.split('/')[0]
                                    let link_html = `<a href="${link}" target="_blank">${link_site}</a>`
                                    googleSearch[0].data[i].link = link_html
                                }
                            }
                            if (link.startsWith('/')) {
                                let link_html = `<a href="https://google.com${link}" target="_blank">google.com</a>`
                                googleSearch[0].data[i].link = link_html
                            }
                        }
                        let image = googleSearch[0].data[i].image
                        if (image) {
                            let image_html = `<img src="${image}" style="max-height:100px">`
                            googleSearch[0].data[i].image = image_html
                        }
                    }
                }
                // if (parsed.length > 0 && parsed[0].part && parsed[0].data && this.all_google_search[parsed[0].part] !== undefined) {
                //     this.all_google_search[parsed[0].part] = parsed[0].data;
                // }
                if (googleSearch?.length > 0 && googleSearch[0]?.part && googleSearch[0]?.data) {
                    this.all_google_search = this.all_google_search || {}
                    this.all_google_search[googleSearch[0].part] = googleSearch[0].data
                }
                this.google_search_key += 1
            },
            setLoading(value) {
                this.loading = value
            },
            async resetSearch() {
                // Close quote modal if it is open/minimized before resetting state.
                this.displayQuoteModal = false
                this.quoteDetais = {}

                this.resetVars()

                this.setTableGoogleSearch([])
                this.setTableBoeingAviall([])
                this.setTableAeroPerformance([])
                this.setTableAeroFastener([])
                this.setTableTextron([])
                this.setTableTextronDefense([])

                this.clearAllPartData()
                this.clearAllTotals()
                this.cleanAllTables()
                this.searchStore.clearVendors()
                this.searchStore.clearPartsSearched()
                this.searchStore.clearSearchActionId()
                this.clearSearchQuantities()
                this.clearMasterSubstitutes()

                this.setPartStatsData([])
                this.setTableQuotes([])
                this.setTableSales([])
                this.setOpenSalesOrdersData({})
                this.setOpenPurchaseOrdersData({})
                this.setTableStats([])
                this.setTableNotes([])
                this.setDisplayData(false)

                this.formData.is_nsn = false
                this.formData.includeGSA = false
                this.formData.parts_text = ''
                this.formData.quantities_text = ''
                this.formData.search_reference = ''
                this.formData.search_type = ''
                this.formData.search_customer = ''
                this.formData.due_date = ''

                // Reset note modal state
                this.$refs.modalAddNote?.resetNote?.()
                this.substitutesStore.resetHighlightedPart()

                this.$router.push('/Dashboard')
            },
            resetVars() {
                // Set store variables
                this.searchStore.set_parts_array([])
                this.searchStore.set_part_selected('')
                this.searchStore.set_part_index(0)

                // Clear Stats Variables
                this.setResetStats()

                // Set local vars
                this.new_search = false
                this.is_nsn = false
                this.duplicate_part_index = -1
                this.duplicate_reference = []
                this.search_action_id = 0
                this.search_type = ''
                this.search_customer = ''
                this.search_reference = ''
                this.alertData = []
                this.bannerData = []
                this.searchDataRaw = []
                this.dbPartDataRaw = []
                this.dbCapPartDataRaw = []
                // this.searchData = []
                this.statsData = []
                this.parts_text = ''
                this.quantities_text = ''
                this.sales_lastyr = 0
                this.sales_currentyr = 0
                this.quotes_lastyr = 0
                this.quotes_currentyr = 0

                this.stream_data = ''
                this.pn_count = ''
                this.upload_files = []
                this.quoteData = []
                this.partQuoted = []
                this.partIndexQuoted = []
                this.quotedResponse = {
                    customer: '',
                    parts: [],
                }
                this.submitQuoteModalStore.cleanState()
                this.research_purchases_only = false
                this.generic_research_only = false
                this.purchaseData = []
                this.pricing_rule_match = []
                this.modal_rule = {}
                this.partsbase_pricing = []
                this.partsbase_market = []
                this.all_fuzzy_part_matches = {}
                this.all_google_search = {}
                this.google_search_key = 0
                this.vendorSearchCompleted = false
                this.stopLiveSearch()
                this.pendingInventoryQuoteRow = null
                // quote values

                // purchase values
                this.p_part_number = ''
                this.p_description = ''
                this.p_quantity = ''
                this.p_unit_of_measure = ''
                this.p_unit_cost = ''
                this.p_margin = ''
                this.p_notes = ''
                //notes
                this.sales_notes = []
                this.admin_notes = []
                // Pricing action data for modal
                this.pa_suggested_price = 0
                this.pa_suggested_margin = 0
                this.pa_description = ''
                this.pa_reason = ''
                this.pa_id = 0
                this.pa_current_quote = 0
                this.pa_current_margin = 0
                this.pendingPricingActionResponse = null
                this.pendingPricingActionPayload = null
                this.pendingPricingActionForm = null
                this.pendingPricingAction = null
                this.bpa_parts = []
                this.gsa_parts = []
                this.gsa_minimums = {}
                if (this.searchStore?.setBpaParts) {
                    this.searchStore.setBpaParts([])
                }
                if (this.searchStore?.setGsaParts) {
                    this.searchStore.setGsaParts([])
                }
                if (this.searchStore?.setGsaMinimums) {
                    this.searchStore.setGsaMinimums({})
                }
                this.userSelectedTab = false
            },
            showfuzzyMatchModal(details) {
                this.moreDetails = details
                const modal = new Modal(document.getElementById('fuzzyMatchModal'))
                modal.show()
            },
            showQuotedResponseModal() {
                const modal = new Modal(document.getElementById('quotedResponseModal'))
                modal.show()
            },
            showPartQuotedModal() {
                const modal = new Modal(document.getElementById('partQuotedModal'))
                modal.show()
            },
            showMoreDetails(details) {
                this.moreDetails = details // Store the details in the data property
                const modal = new Modal(document.getElementById('moreDetailsGenericModal')) // Bootstrap modal instance
                modal.show()
            },
            showMoreDetailsQuotes(details) {
                this.moreDetails = details // Store the details in the data property
                const modal = new Modal(document.getElementById('moreDetailsGenericModal')) // Bootstrap modal instance
                modal.show()
            },
            showMoreDetailsSales(details) {
                this.moreDetails = details // Store the details in the data property
                const modal = new Modal(document.getElementById('moreDetailsGenericModal')) // Bootstrap modal instance
                modal.show()
            },
            handleFormUpdate(updatedData) {
                this.formData = { ...this.formData, ...updatedData }

                if (Object.prototype.hasOwnProperty.call(updatedData, 'search_customer')) {
                    this.setSearchActionCustomer(updatedData.search_customer)
                    this.searchStore.setCustomer(updatedData.search_customer)
                }
                if (Object.prototype.hasOwnProperty.call(updatedData, 'search_reference')) {
                    this.setSearchActionCustomerRef(updatedData.search_reference)
                    this.searchStore.setCustomerRef(updatedData.search_reference)
                }
            },
            handleTopNavbarSearch(rawTerm) {
                const normalizedTerm = (rawTerm || '').trim().toUpperCase()

                if (!normalizedTerm) {
                    return
                }

                this.formData.is_nsn = false
                this.formData.parts_text = normalizedTerm
                this.formData.quantities_text = ''

                this.substitutesStore.fetchMasterSubstitutes(this.formData.parts_text)
                this.search()
            },
            updateSearchQueryParam(searchActionId) {
                if (!searchActionId) {
                    return
                }

                const searchValue = searchActionId.toString()

                if (this.$route.query.search === searchValue) {
                    return
                }

                void this.$router
                    .replace({
                        query: { ...this.$route.query, search: searchValue },
                    })
                    .catch(() => {})
            },
            init_quote_data(parts_array, quantity_array) {
                for (let i = 0; i < parts_array.length; i++) {
                    let quantity = null // quantities are not guaranteed
                    if (quantity_array.length == parts_array.length) {
                        quantity = quantity_array[i]
                    }
                    this.quoteData[i] = {
                        part_number: parts_array[i],
                        quantity: quantity,
                        quote_type: this.search_type,
                        customer: this.search_customer,
                        customer_ref: this.search_reference,
                        no_quote: false,
                        submitted: false,
                        no_quote_reason: '',
                    }
                }
            },
            updateBpaPartsFromQuotes(quoteData, explicitBpaParts = []) {
                const bpaNoteMarker = 'BPA-PART'
                const collectedParts = new Set()
                const minimumMap = {}

                ;(explicitBpaParts || []).forEach((entry) => {
                    const part = entry?.part || entry?.part_number || entry
                    if (part) {
                        collectedParts.add(part)
                        if (
                            entry &&
                            Object.prototype.hasOwnProperty.call(entry, 'minimum_price') &&
                            entry.minimum_price !== undefined
                        ) {
                            minimumMap[part] = entry.minimum_price
                        }
                    }
                })

                Object.values(quoteData || {}).forEach((entry) => {
                    const part = entry?.part
                    ;(entry?.data || []).forEach((item) => {
                        if (
                            item?.no_quote &&
                            (item?.notes || '').toString().toUpperCase().includes(bpaNoteMarker) &&
                            (item?.part_number || part)
                        ) {
                            collectedParts.add(item.part_number || part)
                        }
                    })
                })

                const partsArray = Array.from(collectedParts).filter(Boolean)
                this.bpa_parts = partsArray
                this.bpa_minimums = minimumMap
                if (this.searchStore?.setBpaParts) {
                    this.searchStore.setBpaParts(partsArray)
                }
                if (this.searchStore?.setBpaMinimums) {
                    this.searchStore.setBpaMinimums(minimumMap)
                }
            },
            updateMinimumGSAPriceRanges(gsaParts) {
                const gsaPartNumbers = Object.keys(gsaParts)

                if (this.searchStore?.setGsaParts) {
                    this.searchStore.setGsaParts(gsaPartNumbers)
                }
                if (this.searchStore?.setGsaMinimums) {
                    this.searchStore.setGsaMinimums(gsaParts)
                }
            },
            async loadPartsMinimums(partsArray, customer) {
                if (this.isSearchDemo) {
                    this.updateBpaPartsFromQuotes(this.quoteData, [])
                    this.updateMinimumGSAPriceRanges({})
                    return
                }
                const normalizedParts = Array.isArray(partsArray) ? partsArray.filter(Boolean) : []

                if (!normalizedParts.length) {
                    this.updateBpaPartsFromQuotes(this.quoteData, [])
                    this.updateMinimumGSAPriceRanges({})
                    return
                }

                const response = await SearchService.fetchPartsMinimums(normalizedParts, customer)

                if (!response) {
                    return
                }

                this.updateBpaPartsFromQuotes(this.quoteData, response?.bpa_parts || [])
                this.updateMinimumGSAPriceRanges(response?.gsa_parts || {})
            },
            async featchVendorsData(searchActionId, partNumbers, substitutes) {
                this.loadingMessage = 'Loading Vendors...'

                const responseVendors = await SearchService.fetchVendorsParts(
                    partNumbers,
                    searchActionId,
                    substitutes
                )

                if (!responseVendors || responseVendors.aborted || !responseVendors.reader) {
                    this.loadingParts = false
                    this.loadingMessage = ''
                    return
                }
                this.vendorSearchCompleted = true
                await this.handleVendorsData(responseVendors.reader)
            },
            async loadSearchQuoteData() {
                let localSubstituteQuoteParts = []
                const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                substituteParts.forEach((sub) => {
                    const quoteItems = this.getQuoteData[sub]?.data || []
                    quoteItems.forEach((item) => {
                        localSubstituteQuoteParts.push(item)
                    })
                })

                let localSubstituteSales = []

                substituteParts.forEach((sub) => {
                    const saleItems = this.getSalesData[sub]?.data || []
                    saleItems.forEach((item) => {
                        localSubstituteSales.push(item)
                    })
                })

                this.setTableStats(this.getStatsNumData)

                // const salesNotes = this.sales_notes
                this.setTableNotes(this.getSalesNotes)
            },
            async loadSearchStreammedVendorData() {
                // SET GOOGLE SEARCH DATA
                const dataGoogleSearch = this.getPartDataGoogleSearch
                if (
                    dataGoogleSearch[this.getSelectedPart] &&
                    dataGoogleSearch[this.getSelectedPart].length > 0
                ) {
                    this.setTableGoogleSearch(dataGoogleSearch[this.getSelectedPart])
                }

                const vendors = [
                    'getPartDataBoeing',
                    'getPartDataAeroPerformance',
                    'getPartDataAeroFastener',
                    'getPartDataTextron',
                    'getPartDataTextronDefense',
                ]

                vendors.forEach((vendor) => {
                    const selectedPart = this.getSelectedPart

                    const dataVendorParts = this[vendor]

                    if (dataVendorParts[selectedPart] && dataVendorParts[selectedPart].length > 0) {
                        let dataLocal = dataVendorParts[selectedPart]
                        let localSubstituteBoeing = []
                        const substituteParts = this.getPartsSearched[this.getSelectedPart] || []

                        substituteParts.forEach((sub) => {
                            const getPartDataBoeing = this.getPartDataBoeing[sub]?.data || []
                            getPartDataBoeing.forEach((item) => {
                                localSubstituteBoeing.push(item)
                            })
                        })

                        dataLocal = [...dataLocal, ...localSubstituteBoeing]

                        if (vendor == 'getPartDataBoeing') {
                            this.setTableBoeingAviall(dataLocal)
                        } else if (vendor == 'getPartDataAeroPerformance') {
                            this.setTableAeroPerformance(dataLocal)
                        } else if (vendor == 'getPartDataAeroFastener') {
                            this.setTableAeroFastener(dataLocal)
                        } else if (vendor == 'getPartDataTextron') {
                            this.setTableTextron(dataLocal)
                        } else if (vendor == 'getPartDataTextronDefense') {
                            this.setTableTextronDefense(dataLocal)
                        }
                    }
                })
            },
            async startLiveSearch(parts_array, substitutes) {
                if (this.isResumingSearch) return
                const run = this.beginLiveSearchStream()
                const response = await SearchService.streamLiveSearch(
                    parts_array, run.actionId, substitutes, run.controller.signal
                )
                await this.handleLiveSearchData(response?.reader, run)
            },
            getLiveSearchRowKey(item) {
                if (item?.unique_id_hash) {
                    return `hash:${item.unique_id_hash}`
                }

                return [
                    item?.source,
                    item?.ui_vendor_name,
                    item?.part_number,
                    item?.part_number_manufacturer,
                    item?.substitute_for,
                    item?.vendor_part_number,
                    item?.manufacturer_name,
                    item?.uom,
                    item?.unit_price ?? item?.your_price,
                    item?.qty_in_stock ?? item?.quantity ?? item?.display_qty,
                ]
                    .filter((value) => value !== undefined && value !== null)
                    .join('|')
            },
            dedupeLiveSearchRows(rows) {
                const rowsByKey = new Map()
                ;(rows || []).forEach((row) => {
                    const normalizedRow = normalizeVendorRow(row)
                    const key = this.getLiveSearchRowKey(normalizedRow)
                    if (!key) {
                        return
                    }
                    const existingRow = rowsByKey.get(key)
                    rowsByKey.set(
                        key,
                        existingRow
                            ? this.mergeLiveSearchRows(existingRow, normalizedRow)
                            : normalizedRow
                    )
                })
                return Array.from(rowsByKey.values())
            },
            mergeLiveSearchRows(existingRow, incomingRow) {
                const merged = { ...existingRow, ...incomingRow }
                Object.entries(existingRow || {}).forEach(([key, value]) => {
                    const incomingValue = incomingRow?.[key]
                    if (
                        (incomingValue === undefined ||
                            incomingValue === null ||
                            incomingValue === '') &&
                        value !== undefined &&
                        value !== null &&
                        value !== ''
                    ) {
                        merged[key] = value
                    }
                })
                return merged
            },
            mergeLiveSearchResultsIntoLiveSearchData(results) {
                if (!Array.isArray(results) || results.length === 0) {
                    return false
                }

                const normalizedPartMap = this.buildNormalizedPartMap(
                    this.partsArray,
                    this.substituteParts
                )
                const mergedByPart = {}
                const rowIndexesByPart = {}

                Object.entries(this.getPartsBase || {}).forEach(([part, rows]) => {
                    mergedByPart[part] = [...(rows || [])]
                    rowIndexesByPart[part] = new Map()
                    mergedByPart[part].forEach((row, index) => {
                        const rowKey = this.getLiveSearchRowKey(row)
                        if (rowKey) {
                            rowIndexesByPart[part].set(rowKey, index)
                        }
                    })
                })

                let didMerge = false
                results.forEach((item) => {
                    if (!item) {
                        return
                    }

                    const normalizedItem = normalizeVendorRow(item)
                    const rawPart =
                        normalizedItem.part_number || normalizedItem.part_number_manufacturer
                    if (!rawPart) {
                        return
                    }

                    const cleanedPart = rawPart.includes('=') ? rawPart.split('=')[0] : rawPart
                    const mappedPart =
                        this.resolvePartKey(cleanedPart, normalizedPartMap) || cleanedPart
                    if (!mergedByPart[mappedPart]) {
                        mergedByPart[mappedPart] = []
                        rowIndexesByPart[mappedPart] = new Map()
                    }

                    const existingRows = mergedByPart[mappedPart]
                    const rowIndexes = rowIndexesByPart[mappedPart]
                    const rowKey = this.getLiveSearchRowKey(normalizedItem)
                    const existingRowIndex = rowIndexes.get(rowKey)

                    if (existingRowIndex !== undefined) {
                        existingRows[existingRowIndex] = normalizedItem
                        didMerge = true
                        return
                    }

                    rowIndexes.set(rowKey, existingRows.length)
                    existingRows.push(normalizedItem)
                    didMerge = true
                })

                if (!didMerge) {
                    return false
                }

                Object.entries(mergedByPart).forEach(([part, data]) => {
                    this.setPartData({
                        part,
                        data,
                        vendor: 'Parts Base',
                    })
                })
                this.loadPartsBaseData()
                this.setTabPriority()
                return true
            },
            async handleLiveSearchData(reader, run, offset = 0) {
                await followLiveSearch({
                    reader,
                    flowId: run.flowId, offset,
                    signal: run.controller.signal,
                    fetchStatus: (flowId, offset, signal) =>
                        SearchService.fetchLiveSearchStatus(run.actionId, flowId, offset, signal),
                    onRows: (rows) => this.mergeLiveSearchResultsIntoLiveSearchData(rows),
                    onState: (state) => {
                        this.liveSearchRuns = this.liveSearchRuns.map((item) =>
                            item.controller === run.controller ? { ...item, ...state } : item
                        )
                    },
                })
                if (!run.controller.signal.aborted) {
                    this.loadingParts = false
                    this.loadingMessage = ''
                }
            },
            async loadPartsBaseData() {
                const partsBase = this.getPartsBase

                if (!partsBase || Object.keys(partsBase).length === 0) {
                    return
                }

                let dataLocal = partsBase[this.getSelectedPart] ?? []

                let localSubstitute = []
                const substituteParts = [
                    ...new Set(this.getPartsSearched[this.getSelectedPart] || []),
                ].filter((part) => part && part !== this.getSelectedPart)

                substituteParts.forEach((sub) => {
                    if (partsBase[sub] && partsBase[sub].length > 0) {
                        partsBase[sub].forEach((item) => {
                            localSubstitute.push(item)
                        })
                    }
                })

                dataLocal = [...dataLocal, ...localSubstitute]
                this.setTablePartsBase(this.dedupeLiveSearchRows(dataLocal))
            },
            async reloadNotes() {
                if (this.isSearchDemo) {
                    this.sales_notes = { notes: [] }
                    this.setTableNotes([])
                    return
                }
                let parts = this.getSelectedPart ? [this.getSelectedPart] : this.partsArray
                const response = await NoteService.getUserNotes(
                    parts,
                    this.formData.search_customer,
                    this.search_action_id
                )
                if (!response?.sales_notes) {
                    this.sales_notes = { notes: [] }
                    this.setTableNotes([])
                    return
                }

                this.sales_notes = response.sales_notes

                this.sales_notes.notes = this.sales_notes.notes.filter(
                    (note, index, self) => index === self.findIndex((n) => n.id === note.id)
                )

                this.setTableNotes(this.sales_notes.notes)
            },
            checkSubstituteAvailabilityInStoreAndIfForSameParts(partNumbers) {
                try {
                    const substitutes = this.substitutesStore.master_substitutes
                    if (
                        !substitutes ||
                        (typeof substitutes === 'object' && Object.keys(substitutes).length === 0)
                    ) {
                        return false
                    }
                    const master_subs = substitutes.master_substitutes
                    if (Object.keys(master_subs).length !== partNumbers.length) {
                        return false
                    }
                    const substitutePartNumbers = Object.keys(master_subs)
                    for (let part_number of substitutePartNumbers) {
                        if (!partNumbers.includes(part_number)) return false
                    }
                    return true
                } catch (error) {
                    console.error('Error checking substitutes availability:', error)
                }
            },
            async search() {
                // clean state for new search
                this.searchStore.clearVendors()
                this.searchStore.clearPartsSearched()
                this.clearInventoryData()
                this.clearSearchQuantities()
                this.substituteParts = []
                this.vendorSearchCompleted = false
                this.stopLiveSearch()
                this.pendingInventoryQuoteRow = null

                // get search form data
                const quantities = this.quantity_array
                const searchType = this.formData.search_type
                const customer = (this.formData.search_customer || '').trim()
                const customerRef = this.formData.search_reference
                const dueDate = this.formData.due_date || null
                const partNumbers = this.parsePN(this.formData.parts_text)

                // validations
                if (
                    this.checkSubstituteAvailabilityInStoreAndIfForSameParts(partNumbers) === false
                ) {
                    await this.substitutesStore.fetchMasterSubstitutes(partNumbers)
                }
                const substitutes = this.substitutesStore.master_substitutes
                this.hydrateSubstituteState(substitutes, partNumbers)

                // Retail allows manually typed customers. Ensure the backend has
                // that customer before creating the search action that references it.
                const isCustomerReady = await this.ensureRetailCustomerExists(customer)
                if (!isCustomerReady) {
                    this.loading = false
                    this.loadingParts = false
                    this.loadingMessage = ''
                    return
                }

                this.formData.search_customer = customer

                // update component data
                this.partsArray = partNumbers

                // Update store data
                this.searchStore.set_parts_array(partNumbers)
                this.searchStore.set_part_selected(partNumbers[0])
                this.searchStore.set_part_index(0)
                this.searchStore.setCustomer(customer)
                this.searchStore.setCustomerRef(customerRef)
                this.setSelectedPart(partNumbers[0])
                this.setSelectedPartIndex(0)
                this.setSearchActionCustomer(customer)
                this.setSearchActionCustomerRef(customerRef)
                this.setPartsArray(partNumbers)

                if (!this.isSearchDemo) {
                    this.init_quote_data(partNumbers, quantities)
                }

                const createdSearchAction = await this.createSearchAction(
                    partNumbers,
                    quantities,
                    customer,
                    customerRef,
                    searchType,
                    substitutes,
                    dueDate
                )
                const searchActionId = createdSearchAction?.id

                if (!searchActionId) {
                    this.loading = false
                    this.loadingParts = false
                    this.loadingMessage = ''
                    return
                }

                if (!this.isSearchDemo) {
                    this.loadPartsMinimums(partNumbers, customer)
                }
                this.search_action_id = searchActionId
                this.searchStore.setSearchActionId(searchActionId)
                if (!this.isSearchDemo) {
                    const draftQuoteRows = this.buildDraftQuoteRows(createdSearchAction)
                    if (draftQuoteRows.length > 0) {
                        this.submitQuoteModalStore.setQuotes(draftQuoteRows)
                    } else {
                        await this.submitQuoteModalStore.getQuotesbySearchActionId(searchActionId)
                        this.submitQuoteModalStore.setQuotes(
                            this.hydrateQuoteRowIndexes(this.submitQuoteModalStore.quotes)
                        )
                    }
                    this.setSelectedQuoteIdForIndex(this.getSelectedPartIndex)
                } else {
                    this.submitQuoteModalStore.setQuotes([])
                }
                this.updateSearchQueryParam(searchActionId)
                this.setSearchQuantities(searchActionId, quantities)
                this.partsAndQuantities = this.buildPartsAndQuantities(partNumbers, quantities)

                if (!this.isSearchDemo) {
                    this.fetchQuoteAggregatedData(
                        searchActionId,
                        partNumbers,
                        substitutes,
                        customer
                    )
                    this.fetchInventoryData(searchActionId)
                }
                this.featchVendorsData(searchActionId, partNumbers, substitutes)
                this.startLiveSearch(partNumbers, substitutes)
                this.setTabPriority()
            },
            async ensureRetailCustomerExists(customer) {
                if (!config.getSubdomain || config.getSubdomain() !== 'retail') {
                    return true
                }

                const normalizedCustomer = (customer || '').trim()
                if (!normalizedCustomer) {
                    return false
                }

                try {
                    const response = await CustomerService.ensureCustomerExists(normalizedCustomer)
                    if (response?.data?.success === false) {
                        throw new Error(response.data.error || 'Customer could not be created')
                    }
                    this.dropdownsStore.addCustomer(normalizedCustomer)
                    return true
                } catch (error) {
                    console.error('Failed to create retail customer:', error)
                    toast.error('Could not add retail customer.')
                    return false
                }
            },
            async createSearchAction(
                partNumbers,
                quantities,
                customer,
                customer_ref,
                search_type,
                substitutes,
                dueDate = null
            ) {
                try {
                    this.loadingMessage = 'Searching...'
                    this.loadingParts = true
                    this.setDisplayData(true)

                    const createPayload = {
                        part_numbers: partNumbers,
                        quantity: quantities,
                        customer: customer,
                        customer_ref: customer_ref,
                        quote_type: search_type,
                        substitutes: substitutes,
                        due_date: dueDate,
                    }
                    const response = this.isSearchDemo
                        ? await SearchActionService.createSearchOnlyAction(createPayload)
                        : await SearchActionService.createSearchAction(createPayload)
                    if (response?.data?.success && response.data.data) {
                        console.log('Search action created with ID:', response.data.data.id)
                        return response.data.data
                    }
                    return null
                } catch (error) {
                    console.error('Error creating search action:', error)
                    return null
                } finally {
                    this.loadingMessage = ''
                }
            },

            async fetchQuoteAggregatedData(searchActionId, partNumbers, substitutes, customer) {
                if (this.isSearchDemo) {
                    return
                }
                try {
                    this.loadingMessage = 'Loading quote data...'

                    const response = await QuoteService.getQuoteAggregatedData(
                        searchActionId,
                        partNumbers,
                        substitutes,
                        customer
                    )

                    if (!response) {
                        console.error('Quote search returned no data', response)
                        return
                    }

                    this.quoteData = response.quote_data
                    this.setQuoteData(this.quoteData)

                    this.salesData = response.sales_data
                    this.setSalesData(this.salesData)

                    this.setOpenSalesOrdersData(response.open_sales_orders || {})
                    this.setOpenPurchaseOrdersData(response.open_purchase_orders || {})

                    this.sales_notes = response.sales_notes.notes

                    this.sales_notes = this.sales_notes.filter(
                        (note, index, self) => index === self.findIndex((n) => n.id === note.id)
                    )
                    this.setSalesNotes(this.sales_notes)

                    response.stats_result.forEach(async (item) => {
                        await this.setPartStatsData(item)
                    })

                    this.substitutions = response.substitutions

                    if (response.stats_result?.summary) {
                        this.setStats(response.stats_result.summary)
                    }

                    this.substituteParts = this.substitutions.filter(
                        (sub) => !this.partsArray.includes(sub)
                    )
                    for (const key in response.master_subs_dict) {
                        await this.setPartsSearched({
                            data: response.master_subs_dict[key],
                            part: key,
                        })
                    }

                    await this.loadSearchQuoteData()
                    this.setTabPriority()
                } catch (error) {
                    console.error('Error fetching quote aggregated data:', error)
                } finally {
                    this.loading = false
                    this.loadingMessage = ''
                }
            },
            async fetchInventoryData(searchActionId) {
                if (this.isSearchDemo) {
                    this.setInventoryData({})
                    return
                }
                try {
                    const response =
                        await this.searchStore.loadSearchActionInventory(searchActionId)

                    if (!response) {
                        return
                    }

                    this.setInventoryData(response.inventory || {})
                    this.setTabPriority()
                } catch (error) {
                    console.error('Error fetching inventory data:', error)
                }
            },
            parsePN(parts_text) {
                return parts_text
                    .toUpperCase()
                    .split(/\r?\n/)
                    .map((part) => part.trim())
                    .filter((part) => part !== '')
            },
            async processBannerAlert(parts_array) {
                if (this.isSearchDemo) {
                    return
                }
                const response = await SearchService.getAlertBanner(parts_array)

                if (!response || response.length == 0) {
                    return
                }

                this.bannerAlerts = response

                this.setBanner({
                    message: response[0].text,
                    show: true,
                    type: bannerType.PERSISTENT,
                    onConfirm: () => {
                        this.showBannerAlertModal()
                    },
                })
            },
            showBannerAlertModal() {
                const modal = new Modal(document.getElementById('modalAlertBanner'))
                modal.show()
            },
            async dismissModalAlertBanner() {
                this.setBannerStatus()
            },
            async handleVendorsData(reader) {
                const decoder = new TextDecoder()
                let buffer = ''

                /* eslint-disable */
                try {
                    while (true) {
                        const { done, value } = await reader.read()
                        if (done) break

                        buffer += decoder.decode(value, { stream: true })
                    }
                } catch (e) {
                    if (e.name === 'AbortError') {
                        return
                    }
                    console.error('Error reading vendor stream', e)
                    return
                }

                try {
                    const parsed = JSON.parse(buffer)
                    const normalizedPartMap = this.buildNormalizedPartMap(
                        this.partsArray,
                        this.substituteParts
                    )
                    for (const item of parsed.results) {
                        const normalizedItem = normalizeVendorRow(item)
                        const partNumber = this.resolveVendorPartKey(
                            normalizedItem,
                            normalizedPartMap
                        )

                        if (partNumber && !isInventoryRow(normalizedItem)) {
                            this.appendToVendors({ part: partNumber, data: normalizedItem })
                        }
                    }
                    this.setTabPriority()
                } catch (e) {
                    console.error('Error:', e)
                }
            },
            triggerSearch() {
                this.setDisplayData(true)
            },
            setTab(option, isUserAction = true) {
                if (this.hiddenSearchTabs.includes(option)) {
                    return
                }
                if (isUserAction) {
                    this.userSelectedTab = true
                }
                switch (option) {
                    case 'sales':
                        this.selected = 'Sales'
                        break
                    case 'quotes':
                        this.selected = 'Quotes'
                        break
                    case 'inventory':
                        this.selected = 'Inventory'
                        break
                    case 'open_sales_orders':
                        this.selected = 'Open Sales Orders'
                        break
                    case 'open_purchase_orders':
                        this.selected = 'Open Purchase Orders'
                        break
                    case 'vendor':
                        this.selected = 'Vendor'
                        break
                    case 'ils':
                        this.selected = 'ILS'
                        break
                    case 'marketplace_parts':
                        this.selected = 'Marketplace Parts'
                        break
                    case 'google_search':
                        this.selected = 'Google Search'
                        break
                    case 'parts':
                        this.selected = 'Parts (Various Sources)'
                        break
                    case 'substitutes':
                        this.selected = 'Substitutes'
                        break
                    case 'stats':
                        this.selected = 'Stats'
                        break
                    case 'notes':
                        this.selected = 'Notes'
                        break
                    default:
                        this.selected = 'Unknown Option'
                        break
                }
            },
            normalizeVendorSource(source) {
                return (source || '').toString().trim().toLowerCase()
            },
            isMarketplacePartSource(source) {
                const normalizedSource = this.normalizeVendorSource(source)
                if (!normalizedSource) {
                    return false
                }
                if (normalizedSource === 'ilsmart') {
                    return true
                }
                if (normalizedSource === 'parts_base') {
                    return this.showPartsBase
                }
                return false
            },
            isLiveSearchPartSource(source) {
                const normalizedSource = this.normalizeVendorSource(source)
                if (!normalizedSource) {
                    return false
                }
                if (normalizedSource === 'parts_base' && !this.showPartsBase) {
                    return false
                }
                return !this.isMarketplacePartSource(normalizedSource)
            },
            processResponse(parsed) {
                if (Array.isArray(parsed)) {
                    parsed.forEach((item) => {
                        if (item.site && item.part) {
                            const tabKey = this.getTabKeyBySite(item.site)
                            if (tabKey) {
                                this.tabData[tabKey] = item.data
                            }
                        }
                    })
                }
            },
            getTabKeyBySite(site) {
                const mapping = {
                    Sales: 'sales',
                    Quotes: 'quotes',
                    'Open Sales Orders': 'open_sales_orders',
                    'Open Purchase Orders': 'open_purchase_orders',
                    Vendor: 'vendor',
                    ILS: 'ils',
                    'Marketplace Parts': 'marketplace_parts',
                    'Google Search': 'google_search',
                    'Parts (Various Sources)': 'parts',
                    Substitutes: 'substitutes',
                    Stats: 'stats',
                }
                return mapping[site] || null
            },
        },
    }
</script>

<style>
    .gray-info {
        background-color: var(--color-surface-canvas) !important;
        color: var(--color-text-primary);
    }
    .page-content {
        color: var(--color-text-primary);
    }

    .page-content h3 {
        font-size: 20px;
    }

    .bd-placeholder-img {
        font-size: 1.125rem;
        text-anchor: middle;
        -webkit-user-select: none;
        -moz-user-select: none;
        user-select: none;
    }

    @media (min-width: 768px) {
        .bd-placeholder-img-lg {
            font-size: 3.5rem;
        }
    }

    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    body {
        min-height: 100vh;
        display: grid;
        place-content: center;
        background-color: var(--color-app-bg);
    }

    .modal {
        z-index: 10000 !important;
    }
    .customer-highlight-target {
        color: var(--color-brand-primary);
    }
    .customer-summary-line {
        margin-top: 0.5rem;
        color: var(--color-text-secondary) !important;
    }
    tr.active-part,
    tr.active-part td {
        background-color: var(--color-state-highlight-soft) !important;
    }
</style>
